import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Table, Button, Space, Popconfirm, App, Tag, Tooltip, Modal, Checkbox, Layout, Empty, DatePicker, InputNumber, Select, Spin, Alert, Card, Collapse, Input, Slider, Form, Divider, Dropdown } from "antd";
import { PlusOutlined, DeleteOutlined, WarningOutlined, ClockCircleOutlined, CalculatorOutlined, InsertRowRightOutlined, CopyOutlined, ImportOutlined, SettingOutlined, FileTextOutlined, InfoCircleOutlined, FilterOutlined, ClearOutlined, DownOutlined, CheckOutlined, SelectOutlined, HolderOutlined } from "@ant-design/icons";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { calculateGroundTime, ensureFlightTimeConsistency, formatDuration, pickEditorDepartureTime, pickEditorArrivalTime } from "../../../../utils/timeUtils";
import { applyRouteBlockTimeStaPolicy, getCurrentBlockTimeMinutes } from "../../../../utils/staBlockTimeAlignment";
import { formatFlightTime } from "../../../../utils/timezoneUtils";
import { useTimeZone, getTimezoneByMode } from "../../../../contexts/TimeZoneContext";
import FlightTimeDisplay from "../../../../components/DateTimeDisplay";
import FlightDateDisplay from "../../../../components/DateTimeDisplay/FlightDateDisplay"; // 新增导入
import enhancedApi from "../../../../api/enhancedApi";
import { useTableSelection } from "../../../../hooks";
import { useScheduleEditor } from "../context/ScheduleEditorContext";
import LegSelector from "./LegSelector";
import FlightForm from "./FlightForm";
import { AircraftAvailabilityCalculationService, TimeCalculationService, RotationService, ValidationService, DisplayService, TableService, ConstraintsService, SelectedRangeRecalcService, SameAircraftOverlapService } from "../services";
import { UTCDatePicker } from "../../../../components/DateTimeInput";
import ImportModal from "../../../../components/import/ImportModal";
import { v4 as uuidv4 } from "uuid";
import { flightLegService, flightOperationFlightService } from "../../../../api/enhancedApi";
import AircraftSelector from "../../../../components/common/AircraftSelector";
import { InlineHelpButton } from "../../../../components/common/HelpButton";
import V2TrackImportModal from "./V2TrackImportModal";
import { buildScheduleImportQueryParams, getScheduleImportStatusLabel, SCHEDULE_IMPORT_STATUS_OPTIONS } from "../services/scheduleImportFilters";
import { shouldKeepPaperRotations } from "../../AirlineScheduleIngest/ingestListPresentation";
import RotationVisualizer from "../../../flightOperation/Monitor/components/RotationVisualizer";
import { getAirportMinGroundTime } from "../../../../utils/minGroundTimeUtils";

dayjs.extend(isBetween);
dayjs.extend(timezone);
dayjs.extend(utc);

const DragHandleContext = React.createContext({});

const DraggableRow = (props) => {
    const id = props["data-row-key"];
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: id || "placeholder",
        disabled: !id,
    });

    if (!id) return <tr {...props} />;

    const style = {
        ...props.style,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? { position: "relative", zIndex: 9999, opacity: 0.5, background: "#fafafa" } : {}),
    };

    return (
        <DragHandleContext.Provider value={{ ...attributes, ...listeners }}>
            <tr {...props} ref={setNodeRef} style={style} />
        </DragHandleContext.Provider>
    );
};

const DragHandle = () => {
    const dragHandleProps = React.useContext(DragHandleContext);
    return <HolderOutlined style={{ cursor: "grab", color: "#999", fontSize: 12 }} {...dragHandleProps} />;
};

/**
 * 航班计划表格组件 - 展示和编辑航班计划
 */
const FlightScheduleTable = ({ airportsMap = {} }) => {
    const { state, setFlights, removeFlight, updateFlight, setValidationResults, batchUpdateFlights, setImportFunction, updateCompareResult, setAirportConstraints } = useScheduleEditor();
    const {
        flights,
        schedule,
        validationResults = [],
        constraints: { airportConstraints, aircraftAvailability },
    } = state;
    const { timeZone } = useTimeZone();
    const { message } = App.useApp(); // 使用App.useApp获取message

    const [legSelectorVisible, setLegSelectorVisible] = useState(false);
    const [selectedFlight, setSelectedFlight] = useState(null);
    const [editPopoverVisible, setEditPopoverVisible] = useState(false);
    const [selectedRowIndex, setSelectedRowIndex] = useState(null);
    const [loading, setLoading] = useState(false);
    const [calcModalVisible, setCalcModalVisible] = useState(false);
    const [calcConfig, setCalcConfig] = useState({
        considerMinGroundTime: true,
        considerCurfew: true,
        considerTransportDemand: true,
        minimumImpactMode: true,
        startIndex: null,
    });

    // 添加一个新的状态来处理连续性中断的情况
    const [continuityBreakModal, setContinuityBreakModal] = useState({
        visible: false,
        flightInfo: null,
        resolveCallback: null,
    });

    // 添加复制航班相关状态
    const [copyModalVisible, setCopyModalVisible] = useState(false);
    const [copyStartDate, setCopyStartDate] = useState(null);
    const [copyCount, setCopyCount] = useState(1); // 添加复制次数状态，默认复制1次

    // 添加导入相关状态
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [importType, setImportType] = useState("schedule"); // 'draft' 或 'operation'
    const [scheduleListToImport, setScheduleListToImport] = useState([]);
    const [selectedScheduleIdToImport, setSelectedScheduleIdToImport] = useState(null);
    const [scheduleFlightsToImport, setScheduleFlightsToImport] = useState([]);
    const [importScheduleStatuses, setImportScheduleStatuses] = useState([]);
    const [importScheduleAircraftReg, setImportScheduleAircraftReg] = useState(null);
    const [importScheduleSearchText, setImportScheduleSearchText] = useState("");
    const [loadingSchedulesToImport, setLoadingSchedulesToImport] = useState(false);
    const [loadingFlightsToImport, setLoadingFlightsToImport] = useState(false);
    const [importDateRange, setImportDateRange] = useState({});
    const [operationFlights, setOperationFlights] = useState([]);

    // 添加导入起始日期状态
    const [importStartDate, setImportStartDate] = useState(null);

    // 导入执行中状态与进度（用于在导入弹窗内显示反馈）
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(null); // { current, total }

    // 添加导入时使用的飞机选择状态
    const [importSelectedAircraft, setImportSelectedAircraft] = useState(null);

    // 添加是否导入备注的选项状态
    const [importRemarks, setImportRemarks] = useState(false);

    // 添加行高亮状态和定时器 Ref
    const [highlightedRowIds, setHighlightedRowIds] = useState(new Set());
    // 导入后打的「非默认」标记：只记本次编辑会话，刷新页面不保留；这班改绑到默认后自动不再显示
    const [nonDefaultRouteFlags, setNonDefaultRouteFlags] = useState({});

    // 导入批次计数器（用于区分多次导入，页面刷新后重置）
    const importBatchCounter = useRef(0);

    // 添加通用导入模态框状态
    const [importFromTextVisible, setImportFromTextVisible] = useState(false);
    const [v2TrackImportVisible, setV2TrackImportVisible] = useState(false);

    // 添加筛选相关状态
    const [filterVisible, setFilterVisible] = useState(false);
    const [filterForm] = Form.useForm();
    const [activeFilters, setActiveFilters] = useState({});

    // 正确计算有效筛选条件的数量
    const getActiveFilterCount = useMemo(() => {
        if (!activeFilters || Object.keys(activeFilters).length === 0) {
            return 0;
        }
        // 过滤掉所有无效或空的筛选条件
        return Object.values(activeFilters).filter((value) => {
            if (value === null || value === undefined) return false;
            if (Array.isArray(value) && value.length === 0) return false;
            if (typeof value === "string" && value.trim() === "") return false;
            return true;
        }).length;
    }, [activeFilters]);

    // 获取机场数据和航段数据
    useEffect(() => {
        const fetchMasterData = async () => {
            setLoading(true);
            try {
                // 获取机场数据
                // 检查全局缓存中是否已有机场数据
                if (!window.cachedAirports || window.cachedAirports.length === 0) {
                    if (Object.keys(airportsMap).length > 0) {
                        // 如果外部传入了，则使用外部的，并缓存到全局
                        window.cachedAirports = Object.values(airportsMap);
                    } else {
                        const airportResponse = await enhancedApi.masterData.airport.getAirportList();
                        const airportList = airportResponse?.data?.items || [];
                        if (airportList.length > 0) {
                            window.cachedAirports = airportList;
                        }
                    }
                }

                // 获取航段数据
                if (!window.cachedFlightLegs || window.cachedFlightLegs.length === 0) {
                    const legResponse = await flightLegService.getFlightLegList({ page_size: 500 }); // 添加page_size:500确保获取足够多的航段数据
                    const legList = legResponse?.data?.items || [];
                    if (legList.length > 0) {
                        window.cachedFlightLegs = legList; // 缓存到全局
                    }
                }
            } catch (error) {
                console.error("获取主数据失败:", error);
                message.error("获取机场或航段数据失败");
            } finally {
                setLoading(false);
            }
        };

        // 总是获取数据以确保最新，或者可以根据需要添加条件
        fetchMasterData();
    }, [airportsMap, message]); // 添加 message 作为依赖

    // 高亮指定行的函数
    const highlightRows = useCallback((idsToHighlight) => {
        if (!idsToHighlight || idsToHighlight.length === 0) return;

        // 确保传入的是有效的ID数组
        const validIds = idsToHighlight.filter((id) => id !== undefined && id !== null);
        if (validIds.length === 0) return;

        // 创建新的高亮ID集合（不累积之前的高亮）
        const newIds = new Set(validIds);
        setHighlightedRowIds(newIds);
    }, []); // 依赖为空，因为它只使用 refs 和 setters

    // 计算星期几
    const getDayOfWeek = (dateStr) => {
        return TimeCalculationService.getDayOfWeek(dateStr);
    };

    // 计算时区感知的星期几
    const getTimezoneAwareDayOfWeek = useCallback(
        (record) => {
            if (record.std) {
                try {
                    // 获取目标时区
                    const targetTimezone = getTimezoneByMode(timeZone, record.departure_airport, airportsMap);

                    // 转换到目标时区并计算星期几
                    const stdInTargetTimezone = dayjs.utc(record.std).tz(targetTimezone);
                    const dayOfWeek = stdInTargetTimezone.day();

                    // 转换为1-7 (周一-周日)，dayjs中0是周日
                    return dayOfWeek === 0 ? 7 : dayOfWeek;
                } catch (error) {
                    console.error("计算时区感知星期几失败:", error);
                    // 回退到原有逻辑
                    return getDayOfWeek(record.std);
                }
            } else if (record.flight_date) {
                // 如果没有 std，使用原有逻辑
                return getDayOfWeek(record.flight_date);
            }
            return "";
        },
        [timeZone, airportsMap],
    );

    // 删除航班
    const handleDelete = (id) => {
        try {
            removeFlight(id);
            message.success("航班删除成功");
        } catch (error) {
            console.error("删除航班失败:", error);
            message.error("删除航班失败");
        }
    };

    // 编辑航班
    const handleEdit = (record, rowIndex) => {
        setSelectedFlight(record);
        setSelectedRowIndex(rowIndex);
        setEditPopoverVisible(true);
    };

    // 添加航段
    const handleAddLeg = (index = null) => {
        // index表示要在哪个航班后面插入新航段
        if (index === null && filteredFlights && filteredFlights.length > 0) {
            // 当index为null（即从顶部按钮添加）时，使用最后一个航班的索引
            if (filteredFlights.length > 0) {
                const lastFlightId = filteredFlights[filteredFlights.length - 1].id;
                index = flights.findIndex((f) => f.id === lastFlightId);
            }
        }
        setSelectedRowIndex(index);
        setLegSelectorVisible(true);
    };

    // 处理航段选择结果
    const handleLegSelected = (legs) => {
        if (legs && legs.length > 0) {
            // 使用TableService处理航段数据
            const newFlights = TableService.addLegs(flights, legs, selectedRowIndex, schedule.id);

            // 标记为批量更新
            const massUpdateFlights = newFlights.map((flight) => ({
                ...flight,
                isMassUpdate: true, // 添加标记，表示批量添加
            }));

            // 提取新添加的航班ID用于高亮（仅新添加的，而非所有航班）
            // 获取当前航班列表中的ID集合
            const existingIds = new Set(flights.map((f) => f.id));
            // 过滤出新添加的航班ID
            const newFlightIds = newFlights.filter((flight) => !existingIds.has(flight.id)).map((f) => f.id);

            // 使用批量更新函数
            batchUpdateFlights(massUpdateFlights);

            // 触发高亮，只对新添加的航班进行高亮
            highlightRows(newFlightIds);

            // 如果添加了多个航段，考虑自动计算时间
            if (legs.length > 2) {
                // 找到新添加的航班在数组中的起始位置
                const startIndex = flights.length; // 假设新航班是添加在末尾
                setTimeout(() => {
                    handleAutoCalculate(startIndex);
                }, 500);
            }
        }
        setLegSelectorVisible(false);
    };

    // 清空所有航班
    const handleClearAllFlights = () => {
        const updatedFlights = TableService.clearFlights(flights);
        setFlights(updatedFlights);
        setValidationResults([]);
    };

    // 处理表单提交
    const handleFormSubmit = (values) => {
        try {
            // 查找原始航班数据
            const originalFlight = flights.find((f) => f.id === values.id) || {};

            // 合并原始数据和更新的数据，确保不丢失字段
            const updatedFlight = {
                ...originalFlight,
                ...values,
                // 确保保留原始的display_order字段
                display_order: originalFlight.display_order,
            };

            // 打印日志以便调试
            // console.log("提交航班数据:", {
            //     original: originalFlight,
            //     submitted: values,
            //     merged: updatedFlight,
            // });

            // 使用来自上下文的updateFlight函数更新航班数据
            updateFlight(updatedFlight);
            // 触发高亮
            highlightRows([updatedFlight.id]);
            message.success("航班更新成功");
            setEditPopoverVisible(false);

            // 如果修改了航班起始点、终点或者飞机信息，需要重新计算轮换
            if (!shouldKeepPaperRotations(schedule) && (originalFlight.departure_airport !== updatedFlight.departure_airport || originalFlight.arrival_airport !== updatedFlight.arrival_airport || originalFlight.aircraft_reg !== updatedFlight.aircraft_reg)) {
                recalculateRotations();
            }
        } catch (error) {
            console.error("更新航班失败:", error);
            message.error("更新航班失败，请检查输入");
        }
    };

    // 处理Popover关闭
    const handlePopoverClose = () => {
        // 关闭编辑弹窗
        setEditPopoverVisible(false);
    };

    // 检查航班是否违反约束
    const getFlightViolations = (flightId) => {
        return ValidationService.getFlightViolations(flightId, validationResults);
    };

    // 检查航班对是否违反约束（过站类：本班到达 → 下一班起飞）
    // 校验结果里同一条会同时带前后两班的 id；只认本班作为「前一班」的那条，
    // 否则「上一班 → 本班」的过站问题会错挂到本班行，和本班行显示的过站值对不上
    const getFlightPairViolations = (flightId) => {
        const pairs = ValidationService.getFlightPairViolations(flightId, validationResults) || [];
        return pairs.filter((item) => {
            const ownerId = item.prevFlightId ?? item.flightIds?.[0] ?? item.flights?.[0];
            return ownerId != null && String(ownerId) === String(flightId);
        });
    };

    // 自动计算时间 - 使用防抖优化
    // options.endIndex: 算到该下标（含）后停止（「重算选中」）
    // options.flightsOverride: 指定计算用的完整有序列表（按 display_order），避免筛选/数组顺序错位
    // options.rangeMeta: 选中范围元信息，用于成功提示与缺航路扫描边界
    const [autoCalculateInProgress, setAutoCalculateInProgress] = useState(false);
    const autoCalculateDebounceRef = useRef(null);

    const handleAutoCalculate = useCallback(
        (startIndex = 0, options = {}) => {
            const { endIndex: optionEndIndex = null, flightsOverride = null, rangeMeta = null } = options || {};

            // 防止重复点击
            if (autoCalculateInProgress) return;

            // 清除现有定时器
            if (autoCalculateDebounceRef.current) {
                clearTimeout(autoCalculateDebounceRef.current);
            }

            // 设置防抖
            autoCalculateDebounceRef.current = setTimeout(async () => {
                const sourceFlights = Array.isArray(flightsOverride) && flightsOverride.length > 0 ? flightsOverride : flights;
                const isSelectedRange = rangeMeta != null && optionEndIndex != null;
                const effectiveEndIndex = optionEndIndex != null ? optionEndIndex : (sourceFlights?.length ? sourceFlights.length - 1 : -1);

                // 检查 flights 是否有效以及 startIndex 是否在范围内。
                // 点某一行的"重算"只算它后面的航班（设计如此），点最后一行时后面没有可算的航班，给友好提示
                // 「重算选中」用 baseIndex < endIndex 判断，由调用方先拦截无可算场景
                if (!sourceFlights || sourceFlights.length === 0 || startIndex >= effectiveEndIndex) {
                    if (!isSelectedRange && sourceFlights && sourceFlights.length > 0 && startIndex >= sourceFlights.length - 1) {
                        message.info("已是最后一个航班，后面没有可计算的航班；要调整这一班请点它上一行的重算。");
                    }
                    return;
                }

                try {
                    setAutoCalculateInProgress(true);

                    if (
                        aircraftAvailability?.loading
                        || aircraftAvailability?.fetching
                        || aircraftAvailability?.error
                        || aircraftAvailability?.stale
                    ) {
                        Modal.warning({
                            title: "飞机可用性校验未完成",
                            content: aircraftAvailability?.error?.message
                                || "飞机可用性数据仍在加载或可能已过期，请刷新基础数据后再重算。",
                        });
                        return;
                    }

                    // 获取配置参数
                    const { considerMinGroundTime, considerCurfew, considerTransportDemand, minimumImpactMode } = calcConfig;

                    // 0. 重算前按当前航班列表实际出现的机场重新拉取约束数据（S4）：
                    //    - 本次计算直接用刚拉取的数据，不读闭包里的旧状态
                    //    - 拉取成功后合并回写页面约束状态（不整体替换：保留"可选航段"等
                    //      预加载的、当前航班列表之外机场的约束，避免后续添加航段时校验失效），
                    //      航班列表内机场一律以新数据为准，保证表格校验标记与重算同源
                    //    - 拉取失败（含超时）用页面已有约束继续，不回写、不阻塞重算
                    let constraintsForCalculation = airportConstraints;
                    try {
                        const airportCodes = Array.from(new Set(sourceFlights.flatMap((f) => [f.departure_airport, f.arrival_airport]).filter(Boolean)));
                        const freshConstraints = await ConstraintsService.fetchAirportConstraints(airportCodes);
                        constraintsForCalculation = freshConstraints;
                        setAirportConstraints((prev) => ({ ...prev, ...freshConstraints }));
                    } catch (refreshError) {
                        console.error("重算前刷新机场约束失败:", refreshError);
                        message.warning("获取最新机场约束失败，宵禁数据可能不是最新，将使用已加载的数据继续重算");
                    }

                    // 页面上的轮档和到达先成对记下，供最小影响对照和写回未改时刻的班。
                    // 新轮档只进计算副本，真正重算的班才会带上新轮档和新到达。
                    const originalFlightsCopy = JSON.parse(JSON.stringify(sourceFlights));
                    let flightsForCalculation = JSON.parse(JSON.stringify(sourceFlights));
                    try {
                        const routeIds = Array.from(new Set(flightsForCalculation.map((flight) => flight.flight_route_id).filter(Boolean)));
                        const response = routeIds.length > 0 ? await enhancedApi.flightRoute.getByIds(routeIds) : null;
                        const routesMap = response?.data || {};

                        flightsForCalculation = flightsForCalculation.map((flight) => {
                            if (!flight.flight_route_id) {
                                return {
                                    ...flight,
                                    block_time_minutes: null,
                                    min_block_time_minutes: null,
                                    flight_route_block_time_minutes: null,
                                    flight_route_name: null,
                                };
                            }

                            const route = routesMap[flight.flight_route_id];
                            const routeBlockTime = parseInt(route?.block_time_minutes, 10);
                            const normalizedBlockTime = Number.isNaN(routeBlockTime) || routeBlockTime <= 0 ? null : routeBlockTime;

                            return {
                                ...flight,
                                block_time_minutes: normalizedBlockTime,
                                min_block_time_minutes: normalizedBlockTime,
                                flight_route_block_time_minutes: normalizedBlockTime,
                                flight_route_name: route?.route_name || null,
                            };
                        });
                    } catch (routeRefreshError) {
                        console.error("重算前刷新航路数据失败:", routeRefreshError);
                        message.warning("获取最新航路数据失败，航路数据可能不是最新，将使用已加载的数据继续重算");
                    }

                    // 缺航路预检：行内重算扫到表末；重算选中只扫选中段内会进入链条的班
                    const missingRouteScanEnd = isSelectedRange ? effectiveEndIndex : flightsForCalculation.length - 1;
                    const missingRouteBlockFlights = [];
                    for (let i = startIndex + 1; i <= missingRouteScanEnd; i++) {
                        const prevFlight = flightsForCalculation[i - 1];
                        const currentFlight = flightsForCalculation[i];
                        const shouldRecalculateFlight = prevFlight?.aircraft_reg === currentFlight?.aircraft_reg && prevFlight?.arrival_airport === currentFlight?.departure_airport && prevFlight?.sta;
                        const blockTime = Number(currentFlight?.min_block_time_minutes);

                        if (shouldRecalculateFlight && (!currentFlight.flight_route_id || !Number.isFinite(blockTime) || blockTime <= 0)) {
                            missingRouteBlockFlights.push({ flight: currentFlight, index: i });
                        }
                    }

                    if (missingRouteBlockFlights.length > 0) {
                        Modal.warning({
                            title: "无法重算：缺少航路轮档数据",
                            width: 640,
                            content: (
                                <div>
                                    <p>以下参与重算链的航班缺少绑定航路或有效轮档时间。请先补充航路主数据后再重算。</p>
                                    <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                                        {missingRouteBlockFlights.map(({ flight, index }) => (
                                            <li key={flight.id || index}>
                                                第 {index + 1} 行 {flight.flight_number || "未命名航班"} ({flight.departure_airport || "?"}-{flight.arrival_airport || "?"})
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ),
                        });
                        return;
                    }

                    // 状态里的原始顺序（可能与 display_order 不一致），合并结果时按 id 写回，避免打乱 state 数组顺序
                    const stateFlightsSnapshot = JSON.parse(JSON.stringify(flights));

                    let availabilityForCalculation = {
                        windowsByRegistration: aircraftAvailability?.windowsByRegistration || {},
                        queryStart: aircraftAvailability?.queryStart,
                        queryEnd: aircraftAvailability?.queryEnd,
                    };
                    const calculateFlightTimes = async (calculationStartIndex, overrides = {}) => {
                        const calculationResult = await AircraftAvailabilityCalculationService.calculateFlightTimes(
                            flightsForCalculation,
                            calculationStartIndex,
                            {
                                airportConstraints: constraintsForCalculation,
                                considerMinGroundTime,
                                considerCurfew,
                                considerTransportDemand,
                                transportationDemands: state.parameters?.transportation_demands || [],
                                airportsMap,
                                minimumImpactMode,
                                originalFlights: originalFlightsCopy,
                                aircraftAvailabilityRegistrations: aircraftAvailability?.registrations || [],
                                aircraftAvailabilityWindowsByRegistration: availabilityForCalculation.windowsByRegistration,
                                aircraftAvailabilityQueryStart: availabilityForCalculation.queryStart,
                                aircraftAvailabilityQueryEnd: availabilityForCalculation.queryEnd,
                                ...(isSelectedRange ? { endIndex: effectiveEndIndex } : {}),
                                ...overrides,
                            },
                        );
                        availabilityForCalculation = calculationResult.aircraftAvailabilitySnapshot
                            || availabilityForCalculation;
                        return calculationResult;
                    };

                    // 2. 调用服务计算时间；候选时间跑出已加载范围时，服务会自动补查后重算
                    const result = await calculateFlightTimes(startIndex);

                    if (result.error) {
                        if (result.error.cause) {
                            console.error("重算时补查飞机可用性失败:", result.error.cause);
                        }
                        Modal.error({
                            title: "自动排时未完成",
                            content: result.error.message,
                        });
                        return;
                    }

                    let processedResult = result; // 默认使用初始计算结果
                    let calculationMessage = isSelectedRange
                        ? `已重新计算选中连续段（第 ${rangeMeta.rangeStartIndex + 1}–${rangeMeta.rangeEndIndex + 1} 行）`
                        : `已重新计算从第 ${startIndex + 1} 个航班开始的 ${originalFlightsCopy.length - startIndex} 个航班的时间`; // 默认成功消息
                    let continuedAfterDiscontinuity = false;

                    // 检查是否存在连续性中断
                    if (result.discontinuity) {
                        // 显示连续性中断模态框，等待用户选择
                        const userChoice = await new Promise((resolve) => {
                            setContinuityBreakModal({
                                visible: true,
                                flightInfo: {
                                    prevFlight: result.discontinuity.prevFlight,
                                    currentFlight: result.discontinuity.currentFlight,
                                    index: result.discontinuity.index,
                                },
                                resolveCallback: resolve,
                            });
                        });

                        // 根据用户选择处理
                        if (userChoice === "continue") {
                            // 用户选择忽略并继续，继续计算剩余航班（选中模式仍受 endIndex 约束）
                            const continuedResult = await calculateFlightTimes(
                                result.discontinuity.index - 1,
                                { ignoreFlightContinuity: true },
                            );
                            if (continuedResult.error) {
                                if (continuedResult.error.cause) {
                                    console.error("重算时补查飞机可用性失败:", continuedResult.error.cause);
                                }
                                Modal.error({
                                    title: "自动排时未完成",
                                    content: continuedResult.error.message,
                                });
                                return;
                            }
                            processedResult = continuedResult; // 使用继续计算的结果
                            continuedAfterDiscontinuity = true;
                            calculationMessage = isSelectedRange
                                ? `已重新计算选中连续段（第 ${rangeMeta.rangeStartIndex + 1}–${rangeMeta.rangeEndIndex + 1} 行），并自动处理了连续性中断`
                                : `已重新计算航班时间，并自动处理了连续性中断`;
                        } else {
                            // 用户选择停止计算，只更新已计算的部分 (使用原始 result)
                            calculationMessage = isSelectedRange
                                ? `选中连续段计算在第 ${result.discontinuity.index + 1} 个航班处因连续性中断而停止，之前计算的结果已保存；选中段外航班未改动`
                                : `计算在第 ${result.discontinuity.index + 1} 个航班处因连续性中断而停止，之前计算的结果已保存`;
                        }
                        // 关闭弹窗
                        setContinuityBreakModal({ visible: false, flightInfo: null, resolveCallback: null });
                    }

                    // 最小影响「已赶上进度」：若用户刚选了「继续」处理断链，保留那句说明，避免被追平文案盖掉
                    if (processedResult.caughtUp) {
                        const caughtUpMessage = processedResult.caughtUp.message;
                        if (isSelectedRange) {
                            const rangePrefix = `选中连续段（第 ${rangeMeta.rangeStartIndex + 1}–${rangeMeta.rangeEndIndex + 1} 行）`;
                            calculationMessage = continuedAfterDiscontinuity
                                ? `${rangePrefix}：已处理连续性中断；${caughtUpMessage}`
                                : `${rangePrefix}：${caughtUpMessage}`;
                        } else {
                            calculationMessage = continuedAfterDiscontinuity
                                ? `已处理连续性中断；${caughtUpMessage}`
                                : caughtUpMessage;
                        }
                    }

                    // 3. 整合结果：按 id 写回，保持 state 中 flights 的原始数组顺序
                    const mergeBase = isSelectedRange ? stateFlightsSnapshot : originalFlightsCopy;
                    const finalFlights = TimeCalculationService.mergeCalculatedFlightsKeepingUnchangedPairs(
                        mergeBase,
                        processedResult.flights,
                    );

                    // 3.5 「重算选中」同机时间重叠门禁：试算后、写入前；重叠则整次不写入（含断链继续/停止的最终拟写入结果）
                    if (isSelectedRange) {
                        const orderedForGate = TableService.filterAndSortFlights(finalFlights);
                        const overlapResult = SameAircraftOverlapService.findSameAircraftTimeOverlaps(finalFlights, {
                            orderedFlights: orderedForGate,
                        });
                        const formatGateTime = (value, flight, clockKind = "std") => {
                            if (!value) return "?";
                            // 到达用到达机场时区，起飞用出发机场时区（LOCAL 模式下跨时区航线才对得上）
                            const airportCode =
                                clockKind === "sta" ? flight?.arrival_airport : flight?.departure_airport;
                            const formatted = formatFlightTime(
                                value,
                                airportCode,
                                timeZone,
                                airportsMap,
                                null,
                                "YYYY-MM-DD HH:mm",
                            );
                            return `${formatted.time} ${formatted.label || ""}`.trim();
                        };
                        if (overlapResult.hasOverlap) {
                            const gateMessage =
                                SameAircraftOverlapService.buildRecalcOverlapGateMessage(overlapResult, {
                                    // 用点按钮时传入的勾选 id，避免依赖试算前后 display_order 索引对齐
                                    selectedIds: rangeMeta.selectedIds || [],
                                    formatTime: formatGateTime,
                                }) || "同一架飞机时间冲突，本次重算结果未写入。";
                            // 未写入则清空上次成功重算残留高亮，避免「文案说未写入、行上还亮着」
                            setHighlightedRowIds(new Set());
                            await new Promise((resolve) => {
                                Modal.warning({
                                    title: "同机时间冲突，未写入重算结果",
                                    width: 640,
                                    content: <div style={{ whiteSpace: "pre-wrap" }}>{gateMessage}</div>,
                                    // onOk + afterClose 都会 resolve：点确定走 onOk；点右上角关闭只走 afterClose，避免 Promise 挂起。第二次 resolve 无害。
                                    onOk: () => resolve(),
                                    afterClose: () => resolve(),
                                });
                            });
                            return;
                        }
                        // 空注册号 / 缺时刻：不得静默放行；与保存路径一致，允许取消不写入
                        if (SameAircraftOverlapService.shouldWarnUncheckedRegistration(overlapResult, finalFlights.length)) {
                            const uncheckedNote =
                                SameAircraftOverlapService.buildUncheckedRegistrationNote(overlapResult) ||
                                "部分航班未填注册号，无法按飞机完整检查。";
                            const incompleteNote = SameAircraftOverlapService.buildSkippedIncompleteNote(overlapResult);
                            const coverageMessage = incompleteNote
                                ? `${uncheckedNote}\n\n${incompleteNote}\n\n可先补全注册号/时刻后再次重算，或改用行内「重算」谨慎核对。`
                                : `${uncheckedNote}\n\n可先补全注册号后再次重算，或改用行内「重算」谨慎核对。`;
                            const continueWrite = await new Promise((resolve) => {
                                Modal.confirm({
                                    title: "部分航班无法按飞机检查",
                                    width: 640,
                                    content: <div style={{ whiteSpace: "pre-wrap" }}>{coverageMessage}</div>,
                                    okText: "仍要重算",
                                    cancelText: "先去补注册号",
                                    onOk: () => resolve(true),
                                    onCancel: () => resolve(false),
                                });
                            });
                            if (!continueWrite) {
                                setHighlightedRowIds(new Set());
                                return;
                            }
                        } else if ((overlapResult.skippedIncompleteCount || 0) > 0) {
                            const incompleteNote = SameAircraftOverlapService.buildSkippedIncompleteNote(overlapResult);
                            if (incompleteNote) {
                                const continueWrite = await new Promise((resolve) => {
                                    Modal.confirm({
                                        title: "部分航班未参与同机检查",
                                        width: 640,
                                        content: (
                                            <div style={{ whiteSpace: "pre-wrap" }}>
                                                {incompleteNote}
                                                {"\n\n"}
                                                可先补全时刻后再次重算。
                                            </div>
                                        ),
                                        okText: "仍要重算",
                                        cancelText: "先去补时刻",
                                        onOk: () => resolve(true),
                                        onCancel: () => resolve(false),
                                    });
                                });
                                if (!continueWrite) {
                                    setHighlightedRowIds(new Set());
                                    return;
                                }
                            }
                        }
                    }

                    // 4. 使用保持了原始顺序的 finalFlights 批量更新状态
                    batchUpdateFlights(finalFlights);

                    // 5. 高亮被修改的行 (与 finalFlights 和重算前快照对比)
                    const compareSnapshot = isSelectedRange ? stateFlightsSnapshot : originalFlightsCopy;
                    const modifiedFlightIds = finalFlights
                        .filter((flight) => {
                            const original = compareSnapshot.find((of) => of.id === flight.id);
                            return original && (original.std !== flight.std || original.sta !== flight.sta || original.flight_date !== flight.flight_date);
                        })
                        .map((flight) => flight.id);

                    // 选中范围成功提示：班数按实际被改时间的航班统计，不得沿用「从基准数到表末」
                    if (isSelectedRange && !processedResult.caughtUp) {
                        const rangeLabel = `第 ${rangeMeta.rangeStartIndex + 1}–${rangeMeta.rangeEndIndex + 1} 行`;
                        if (result.discontinuity) {
                            // 断链分支已写好 calculationMessage，仅补上实际改动班数
                            calculationMessage = `${calculationMessage}（实际更新 ${modifiedFlightIds.length} 个航班）`;
                        } else {
                            calculationMessage = `已重新计算选中连续段（${rangeLabel}），实际更新 ${modifiedFlightIds.length} 个航班的时间`;
                        }
                    }

                    // 确保高亮在 DOM 更新后执行
                    setTimeout(() => {
                        highlightRows(modifiedFlightIds);
                    }, 100);

                    message.success(calculationMessage);
                } catch (error) {
                    console.error("自动计算时间失败:", error);
                    message.error("自动计算时间失败: " + (error.message || "未知错误"));
                } finally {
                    setAutoCalculateInProgress(false);
                    // 确保在 finally 中关闭弹窗，以防异常发生时未关闭
                    if (continuityBreakModal.visible) {
                        setContinuityBreakModal({ visible: false, flightInfo: null, resolveCallback: null });
                    }
                }
            }, 300); // 防抖时间保持 300ms
        },
        [flights, calcConfig, state.parameters, airportConstraints, aircraftAvailability, airportsMap, timeZone, batchUpdateFlights, autoCalculateInProgress, setContinuityBreakModal, highlightRows, message, continuityBreakModal.visible, setAirportConstraints], // 添加 message 和 continuityBreakModal.visible 作为依赖
    );

    // 处理用户选择连续性处理方式
    const handleContinuityBreakAction = (action) => {
        // 调用回调函数并传递用户选择
        if (continuityBreakModal.resolveCallback) {
            continuityBreakModal.resolveCallback(action);
        }

        // 关闭对话框
        setContinuityBreakModal({
            visible: false,
            flightInfo: null,
            resolveCallback: null,
        });
    };

    // 优化复制航班操作
    const handleCopyFlightsExecute = () => {
        if (!selectedRows || selectedRows.length === 0 || !copyStartDate) {
            message.error("请选择要复制的航班并设置开始日期");
            return;
        }

        try {
            setLoading(true);
            // 获取复制次数
            const count = copyCount || 1;

            // 按照表格中的显示顺序对选中的航班进行排序
            const sortedSelectedRows = [...selectedRows].sort((a, b) => {
                const aIndex = filteredFlights.findIndex((f) => f.id === a.id);
                const bIndex = filteredFlights.findIndex((f) => f.id === b.id);
                return aIndex - bIndex;
            });

            // 直接调用TableService.copyFlights一次，传入所有必要参数
            const newFlights = TableService.copyFlights(
                flights, // 当前航班列表
                sortedSelectedRows, // 按表格顺序排序的选中航班
                {
                    copyStartDate: copyStartDate.format("YYYY-MM-DD"), // 起始日期
                    copyCount: count, // 复制次数（可能多次）
                },
                schedule.id, // 计划ID
            );

            // 标记为批量更新
            const flightsWithUpdateMark = newFlights.map((flight) => ({
                ...flight,
                isMassUpdate: true, // 添加批量更新标记
            }));

            // 获取新添加的航班ID列表
            const existingIds = new Set(flights.map((f) => f.id));
            const newFlightIds = flightsWithUpdateMark.filter((flight) => !existingIds.has(flight.id)).map((flight) => flight.id);

            // 使用批量更新函数更新航班列表
            batchUpdateFlights(flightsWithUpdateMark);

            // 触发高亮显示新添加的航班
            highlightRows(newFlightIds);

            message.success(`成功复制 ${selectedRows.length} 个航班 ${count} 次`);
            // 清除选择
            clearSelection();
        } catch (error) {
            console.error("复制航班失败:", error);
            message.error("复制航班失败: " + error.message);
        } finally {
            setLoading(false);
            setCopyModalVisible(false);
        }
    };

    const searchTimerRef = useRef(null);

    const scheduleFetchRequestRef = useRef(0);

    const fetchSchedules = async (search, limit, filterOverrides = {}) => {
        const requestId = scheduleFetchRequestRef.current + 1;
        scheduleFetchRequestRef.current = requestId;
        try {
            setLoadingSchedulesToImport(true);
            const params = buildScheduleImportQueryParams({
                search,
                limit,
                statuses: filterOverrides.statuses ?? importScheduleStatuses,
                aircraftReg: filterOverrides.aircraftReg ?? importScheduleAircraftReg,
            });
            const response = await enhancedApi.schedule.getScheduleListSimple(params);
            if (requestId !== scheduleFetchRequestRef.current) return;
            const schedules = response?.data || [];
            setScheduleListToImport(schedules.filter((s) => s.id !== schedule?.id));
        } catch (error) {
            if (requestId !== scheduleFetchRequestRef.current) return;
            console.error("获取计划列表失败:", error);
            message.error("获取计划列表失败");
        } finally {
            if (requestId === scheduleFetchRequestRef.current) {
                setLoadingSchedulesToImport(false);
            }
        }
    };

    const resetSelectedImportSchedule = () => {
        setSelectedScheduleIdToImport(null);
        setScheduleFlightsToImport([]);
    };

    const handleImportScheduleStatusesChange = (statuses) => {
        setImportScheduleStatuses(statuses);
        resetSelectedImportSchedule();
        fetchSchedules(importScheduleSearchText, 50, { statuses });
    };

    const handleImportScheduleAircraftChange = (aircraftReg) => {
        setImportScheduleAircraftReg(aircraftReg || null);
        resetSelectedImportSchedule();
        fetchSchedules(importScheduleSearchText, 50, { aircraftReg: aircraftReg || "" });
    };

    // 获取选定草稿的航班
    const fetchScheduleFlights = async (scheduleId) => {
        if (!scheduleId) return;

        try {
            setLoadingFlightsToImport(true);
            const response = await enhancedApi.schedule.getScheduleFlights(scheduleId, { page_size: 500 }); // 使用page_size参数获取足够多的航班数据
            const flights = response?.data?.items || [];
            setScheduleFlightsToImport(flights);
        } catch (error) {
            console.error(`获取草稿计划 ${scheduleId} 的航班列表失败:`, error);
            message.error("获取草稿航班列表失败");
            setScheduleFlightsToImport([]);
        } finally {
            setLoadingFlightsToImport(false);
        }
    };

    // 使用 useMemo 计算当前航班列表中的最小和最大日期
    const [minFlightDate, maxFlightDate] = useMemo(() => {
        if (!flights || flights.length === 0) {
            // 如果没有航班，可以返回 null 或计划的默认日期
            return [null, null];
            // 或者 return [schedule?.start_date ? dayjs.utc(schedule.start_date) : null, schedule?.end_date ? dayjs.utc(schedule.end_date) : null];
        }
        let minDate = null;
        let maxDate = null;
        flights.forEach((flight) => {
            if (flight.flight_date) {
                const currentDate = dayjs.utc(flight.flight_date);
                if (currentDate.isValid()) {
                    if (!minDate || currentDate.isBefore(minDate)) {
                        minDate = currentDate;
                    }
                    if (!maxDate || currentDate.isAfter(maxDate)) {
                        maxDate = currentDate;
                    }
                }
            }
        });
        return [minDate, maxDate];
    }, [flights /*, schedule.start_date, schedule.end_date */]); // 根据需要决定是否依赖 schedule 的日期

    const operationImportDateOffsetDays = useMemo(() => TableService.calculateImportDateOffsetDays(operationFlights, importStartDate), [operationFlights, importStartDate]);

    // 获取执行计划航班
    const fetchOperationFlights = async (paramsOverride = {}) => {
        const aircraftReg = paramsOverride.aircraftReg || importSelectedAircraft;

        // 如果是手动触发且没有选择飞机，则提示
        if (!paramsOverride.aircraftReg && importType === "operation" && !importSelectedAircraft) {
            message.warning("请先选择飞机才能导入执行计划");
            return;
        }

        // 优先用参数覆盖，否则用importDateRange，否则用min/max
        let startDate = paramsOverride.startDate || importDateRange.startDate || minFlightDate;
        let endDate = paramsOverride.endDate || importDateRange.endDate || maxFlightDate;

        if (!startDate || !endDate) {
            message.warning("当前计划中无有效航班日期，无法查询执行计划。");
            setOperationFlights([]);
            return;
        }

        try {
            setLoadingFlightsToImport(true);
            const params = {
                aircraft_reg: aircraftReg || schedule.aircraft?.registration,
                status: ["SCHEDULED", "DELAYED", "DEPARTED", "COMPLETED"].join(","),
                page_size: 500,
                start_date: dayjs(startDate).format("YYYY-MM-DD"),
                end_date: dayjs(endDate).format("YYYY-MM-DD"),
                date_basis: "planned",
            };
            const response = await flightOperationFlightService.getFlightOperations(params);
            // 根据API_GUIDELINES.md规范，后端必须返回PaginatedResponse格式
            if (!response || !response.data || !response.data.items || typeof response.data.total !== "number") {
                throw new Error("后端API响应不符合PaginatedResponse规范，请检查API实现");
            }
            setOperationFlights(response.data.items);
        } catch (error) {
            console.error("获取执行计划航班失败:", error);
            message.error("获取执行计划航班失败");
            setOperationFlights([]);
        } finally {
            setLoadingFlightsToImport(false);
        }
    };

    // 处理打开导入模态框
    const handleOpenImportModal = (type) => {
        setImportType(type);
        setImportModalVisible(true);
        setSelectedScheduleIdToImport(null);
        setScheduleFlightsToImport([]);
        setOperationFlights([]);

        // 自动填写导入起始日期为计划开始日期
        const planStartDate = schedule.start_date ? dayjs(schedule.start_date) : null;
        setImportStartDate(planStartDate);

        setImportSelectedAircraft(schedule.aircraft?.registration || null);
        setImportRemarks(true);

        if (type === "schedule") {
            setImportScheduleSearchText("");
            fetchSchedules("", 50);
        } else {
            // 优先使用计划日期范围作为默认查询范围
            const planEndDate = schedule.end_date ? dayjs(schedule.end_date) : null;

            let queryStartDate = minFlightDate;
            let queryEndDate = maxFlightDate;

            if (planStartDate && planEndDate) {
                queryStartDate = planStartDate;
                queryEndDate = planEndDate;

                setImportDateRange({
                    startDate: planStartDate,
                    endDate: planEndDate,
                });
            } else if (minFlightDate && maxFlightDate) {
                // 如果没有计划日期，则回退到当前航班的最小/最大日期
                setImportDateRange({
                    startDate: minFlightDate,
                    endDate: maxFlightDate,
                });
            } else {
                setImportDateRange({});
            }

            // 立即触发查询
            // 此时状态更新(setImportSelectedAircraft等)可能尚未生效，所以我们需要显式传递参数
            const aircraftToUse = schedule.aircraft?.registration || null;
            if (aircraftToUse && queryStartDate && queryEndDate) {
                fetchOperationFlights({
                    aircraftReg: aircraftToUse,
                    startDate: queryStartDate,
                    endDate: queryEndDate,
                });
            }
        }
    };

    // 处理选择草稿计划
    const handleSelectSchedule = (scheduleId) => {
        setSelectedScheduleIdToImport(scheduleId);
        fetchScheduleFlights(scheduleId);
    };

    // 处理日期范围变更并自动触发查询
    const handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            const newStartDate = dates[0];
            const newEndDate = dates[1];
            setImportDateRange({
                startDate: newStartDate,
                endDate: newEndDate,
            });
            // 自动触发查询（如果有飞机选择）
            if (importSelectedAircraft) {
                fetchOperationFlights({
                    aircraftReg: importSelectedAircraft,
                    startDate: newStartDate,
                    endDate: newEndDate,
                });
            }
        } else {
            setImportDateRange({});
        }
    };

    // 处理导入飞机选择变更并自动触发查询
    const handleImportAircraftChange = (value) => {
        setImportSelectedAircraft(value);
        // 自动触发查询（如果有日期范围）
        if (value && importDateRange.startDate && importDateRange.endDate) {
            fetchOperationFlights({
                aircraftReg: value,
                startDate: importDateRange.startDate,
                endDate: importDateRange.endDate,
            });
        }
    };

    // 处理导入航班 (从草稿/执行计划)
    const handleImportFlights = () => {
        try {
            let flightsToImport = [];

            if (importType === "schedule") {
                if (scheduleFlightsToImport.length === 0) {
                    message.warning("没有可导入的航班");
                    return;
                }
                flightsToImport = scheduleFlightsToImport;
            } else {
                if (operationFlights.length === 0) {
                    message.warning("没有可导入的航班");
                    return;
                }
                flightsToImport = operationFlights;
            }

            if (!importStartDate) {
                message.warning("请先设置导入起始日期");
                return;
            }

            setLoading(true);
            setImporting(true);
            setImportProgress(null);

            (async () => {
                try {
                    let legData = [];
                    try {
                        const response = await enhancedApi.flightLeg.getFlightLegList({ page_size: 500 }); // 添加page_size:500确保获取足够多的航段数据
                        const legList = response?.data?.items || [];
                        if (legList.length > 0) {
                            legData = legList;
                        }
                    } catch (error) {
                        console.error("获取航段数据失败:", error);
                        message.warning("获取航段数据失败，将使用默认的轮档时间和过站时间");
                    }

                    // 递增导入批次计数器
                    importBatchCounter.current += 1;
                    const currentBatch = importBatchCounter.current;

                    let newFlights = await TableService.importFlights(
                        flights,
                        flightsToImport,
                        {
                            importStartDate,
                            scheduleId: schedule.id,
                            state, // 传递 state 包含 aircraft 信息
                            importType,
                            importRemarks, // 传递是否导入备注的选项
                            importBatch: currentBatch, // 传递导入批次号
                            onProgress: (current, total) => setImportProgress({ current, total }), // 进度回调
                            setAirportConstraints, // 导入拉到的新约束回写页面状态
                            // 沿用源航路且与当前默认不一致的班：表格航路列打「非默认」标记
                            onNonDefaultRoutes: (items) => {
                                setNonDefaultRouteFlags((prev) => {
                                    const next = { ...prev };
                                    items.forEach((item) => {
                                        next[item.flight_id] = item;
                                    });
                                    return next;
                                });
                            },
                        },
                        legData,
                    );

                    // 强制使用当前计划上下文中的飞机注册号
                    const aircraftRegToUse = schedule.aircraft?.registration || "";

                    newFlights = newFlights.map((flight) => {
                        const { aircraft_type, ...restFlight } = flight; // 移除 aircraft_type
                        return {
                            ...restFlight,
                            aircraft_reg: aircraftRegToUse, // 使用选择的或当前的飞机注册号
                            // 保留 _importBatch 标记（如果存在）
                        };
                    });

                    setFlights(newFlights);
                    const currentIds = new Set(flights.map((f) => f.id));
                    const importedFlightIds = newFlights.filter((f) => !currentIds.has(f.id)).map((f) => f.id);
                    highlightRows(importedFlightIds);

                    setImportModalVisible(false);
                } catch (error) {
                    console.error("处理导入航班时出错:", error);
                    message.error("导入航班失败");
                } finally {
                    setLoading(false);
                    setImporting(false);
                    setImportProgress(null);
                }
            })();
        } catch (error) {
            console.error("导入航班失败:", error);
            message.error("导入航班失败");
            setLoading(false);
            setImporting(false);
            setImportProgress(null);
        }
    };

    const handleImportV2TrackRows = useCallback(
        (rows) => {
            if (!Array.isArray(rows) || rows.length === 0) {
                message.warning("没有可导入的 V2 编辑轨航班");
                return;
            }

            importBatchCounter.current += 1;
            const currentBatch = importBatchCounter.current;
            const maxDisplayOrder = flights.length > 0 ? Math.max(...flights.map((flight) => Number(flight.display_order) || 0)) : 0;
            const existingIds = new Set(flights.map((flight) => flight.id));

            const importedRows = rows.map((row, index) => {
                let rowId;
                do {
                    rowId = TableService.generateUniqueId(`v2track${index}`, row.source_v2_flight_id || row.id || "");
                } while (existingIds.has(rowId));
                existingIds.add(rowId);

                const imported = {
                    ...row,
                    id: rowId,
                    schedule_id: schedule.id,
                    display_order: maxDisplayOrder + index + 1,
                    _importBatch: currentBatch,
                };
                const matchedBh = getCurrentBlockTimeMinutes(imported);
                return matchedBh ? applyRouteBlockTimeStaPolicy(imported, matchedBh) : imported;
            });

            const nextFlights = [...flights, ...importedRows].map((flight, index) => ({
                ...flight,
                display_order: Number(flight.display_order) || index + 1,
            }));

            setFlights(nextFlights);
            updateCompareResult(null);
            highlightRows(importedRows.map((row) => row.id));
            setV2TrackImportVisible(false);
            const rewritten = importedRows.filter((row, index) => rows[index]?.sta && row.sta && rows[index].sta !== row.sta);
            message.success(`已从 V2 编辑轨导入 ${importedRows.length} 个航班，请保存并重新对比后再发布`);
            if (rewritten.length > 0) {
                message.info(`已按匹配轮档更新 ${rewritten.length} 班到达（起飞没动）`, 5);
            }
        },
        [flights, schedule.id, setFlights, updateCompareResult, highlightRows, message],
    );

    // 处理通用导入解析结果
    const handleDataParsedFromImport = useCallback(
        async (parsedData) => {
            try {
                if (!Array.isArray(parsedData) || parsedData.length === 0) {
                    message.error("解析数据格式不正确或为空");
                    return;
                }

                setLoading(true); // 显示加载状态

                // 1. 预处理航段数据（假设已加载到 window.cachedFlightLegs）
                const flightLegsMap = (window.cachedFlightLegs || []).reduce((map, leg) => {
                    // 使用更健壮的key，处理可能的null或undefined
                    const dep = leg.departure_airport || "UNKNOWN_DEP";
                    const arr = leg.arrival_airport || "UNKNOWN_ARR";
                    map[`${dep}-${arr}`] = leg;
                    return map;
                }, {});

                // 2. 获取机场约束：导入前按涉及机场读取主数据当前值，
                //    导入不必先点“刷新基础数据”，与加航段、重算同一口径；
                //    拉取失败退回页面已加载的约束
                let constraintsForImport = state.constraints?.airportConstraints || {};
                const missingGroundTimeAirports = new Set();
                try {
                    const importAirportCodes = Array.from(new Set(parsedData.flatMap((f) => [f.departure_airport, f.arrival_airport]).filter(Boolean)));
                    if (importAirportCodes.length > 0) {
                        const freshConstraints = await ConstraintsService.fetchAirportConstraints(importAirportCodes);
                        constraintsForImport = { ...constraintsForImport, ...freshConstraints };
                        // 回写页面约束状态：导入后表格展示的最小过站与本次导入同源，
                        // 不必等切走切回才刷新
                        setAirportConstraints((prev) => ({ ...prev, ...freshConstraints }));
                    }
                } catch (refreshError) {
                    console.error("Excel导入前刷新机场约束失败:", refreshError);
                }

                const flightsData = parsedData;
                const currentAircraftReg = schedule.aircraft?.registration || ""; // 获取当前注册号

                // 【航段航路BH优化】记录导入失败的航段
                const failedLegs = [];
                const successfulFlights = [];
                const invalidRouteFlights = []; // 记录因缺少航路而验证失败的航班
                let excelStaRewriteCount = 0;

                console.log(`[Excel导入校验] 开始处理 ${flightsData.length} 个航班...`);
                console.log(`[Excel导入校验] 当前飞机注册号: ${currentAircraftReg || "未选择"}`);

                // 记录当前航班的ID集合，用于防止ID冲突
                const existingFlightIds = new Set(flights.map((f) => f.id));

                // 计算当前最大的display_order
                const maxDisplayOrder = flights.length > 0 ? Math.max(...flights.filter((f) => typeof f.display_order === "number").map((f) => f.display_order || 0)) : 0;

                // 【航段航路BH优化】改为for循环以支持异步航路匹配和跳过逻辑
                for (let index = 0; index < flightsData.length; index++) {
                    const flight = flightsData[index];
                    // 确保每次生成的ID都是唯一的，避免与现有ID冲突
                    let flightId;
                    do {
                        flightId = uuidv4();
                    } while (existingFlightIds.has(flightId));

                    existingFlightIds.add(flightId); // 添加到集合中防止后续重复

                    // 尝试解析导入的 std 和 sta，确保是 UTC
                    const std = flight.std ? dayjs.utc(flight.std) : null;
                    const sta = flight.sta ? dayjs.utc(flight.sta) : null;
                    // 从 std 推断 flight_date (UTC)
                    const flightDate = std?.isValid() ? std.format("YYYY-MM-DD") : null;

                    // 6. 设置最小地面时间 (min_ground_time_minutes)：只认到达机场主数据的
                    //    明确配置（含 0 分钟）；机场没配就留空并标记该行，不得写 60，
                    //    也不得沿用导入数据里带的旧数字
                    const minGroundTimeMinutes = getAirportMinGroundTime(constraintsForImport, flight.arrival_airport);
                    if (minGroundTimeMinutes === null) {
                        missingGroundTimeAirports.add(flight.arrival_airport || "未知机场");
                    }

                    // 移除 aircraft_type 并强制 aircraft_reg
                    const { aircraft_type, aircraft_reg, ...restFlight } = flight;

                    // 【航段航路BH优化】强制匹配航路，失败则跳过该航班
                    let routeMatch = null;

                    // 必须有起降站信息
                    if (!flight.departure_airport || !flight.arrival_airport) {
                        failedLegs.push({
                            flight_number: flight.flight_number || `Excel第${index + 1}行`,
                            leg: `${flight.departure_airport || "?"}-${flight.arrival_airport || "?"}`,
                            reason: "缺少起降站信息",
                        });
                        continue; // 跳过此航班
                    }

                    // 尝试匹配航路
                    if (currentAircraftReg) {
                        try {
                            const response = await enhancedApi.flightRoute.getBestMatchRoute(flight.departure_airport, flight.arrival_airport, currentAircraftReg);
                            const route = response?.data;
                            if (route && route.block_time_minutes) {
                                routeMatch = {
                                    flight_route_id: route.id,
                                    min_block_time_minutes: route.block_time_minutes,
                                    route_name: route.route_name,
                                };
                            }
                        } catch (error) {
                            console.warn(`[Excel导入] 为航班 ${flight.flight_number} 匹配航路失败:`, error);
                        }
                    }

                    // 【核心校验】如果没有匹配到有效航路，记录失败
                    if (!routeMatch || !routeMatch.min_block_time_minutes) {
                        const failureReason = currentAircraftReg ? "无可用航路" : "未选择飞机，无法匹配航路";
                        console.warn(`[Excel导入] 航班 ${flight.flight_number} (${flight.departure_airport}-${flight.arrival_airport}) 验证失败: ${failureReason}`);

                        failedLegs.push({
                            flight_number: flight.flight_number || `Excel第${index + 1}行`,
                            leg: `${flight.departure_airport}-${flight.arrival_airport}`,
                            reason: failureReason,
                        });

                        // 创建包含基本信息的航班对象（无航路信息）
                        let tempFlightNoRoute = {
                            ...restFlight,
                            id: flightId,
                            schedule_id: schedule.id,
                            flight_date: flightDate,
                            std: std?.isValid() ? std.format("YYYY-MM-DDTHH:mm:ss") + "Z" : null,
                            sta: sta?.isValid() ? sta.format("YYYY-MM-DDTHH:mm:ss") + "Z" : null,
                            isNew: true,
                            display_order: maxDisplayOrder + successfulFlights.length + invalidRouteFlights.length + 1,
                            aircraft_reg: currentAircraftReg,
                            block_time_minutes: null,
                            min_block_time_minutes: null,
                            min_ground_time_minutes: minGroundTimeMinutes,
                            // 航路信息为空
                            flight_route_id: null,
                            flight_route_name: null,
                            flight_route_block_time_minutes: null,
                            day_of_week: std?.isValid() ? std.day() || 7 : null,
                        };

                        tempFlightNoRoute = ensureFlightTimeConsistency(tempFlightNoRoute);

                        invalidRouteFlights.push(tempFlightNoRoute);
                        continue; // 跳过后续正常处理，进入下一个循环
                    }

                    // 调试日志：确认航路匹配成功
                    console.log(`[Excel导入] ✅ 航班 ${flight.flight_number} (${flight.departure_airport}-${flight.arrival_airport}) 匹配到航路: ${routeMatch.route_name} (BH: ${routeMatch.min_block_time_minutes}分钟)`);

                    // 【航段航路BH优化】使用航路的BH，移除从导入数据或航段获取BH的备用逻辑
                    const minBlockTimeMinutes = parseInt(routeMatch.min_block_time_minutes);

                    // 创建航班临时对象，包含所有必要字段
                    let tempFlight = {
                        ...restFlight,
                        id: flightId,
                        schedule_id: schedule.id,
                        flight_date: flightDate, // 使用从 std 推断的 UTC 日期
                        std: std?.isValid() ? std.format("YYYY-MM-DDTHH:mm:ss") + "Z" : null,
                        sta: sta?.isValid() ? sta.format("YYYY-MM-DDTHH:mm:ss") + "Z" : null,
                        isNew: true, // 标记为新导入
                        display_order: maxDisplayOrder + successfulFlights.length + invalidRouteFlights.length + 1, // 使用成功航班数量计算顺序
                        aircraft_reg: currentAircraftReg, // 强制使用当前上下文的注册号
                        min_block_time_minutes: minBlockTimeMinutes, // 使用航路的BH
                        block_time_minutes: minBlockTimeMinutes,
                        min_ground_time_minutes: minGroundTimeMinutes,
                        // 设置航路信息
                        flight_route_id: routeMatch.flight_route_id,
                        flight_route_name: routeMatch.route_name,
                        flight_route_block_time_minutes: routeMatch.min_block_time_minutes,
                        // 确保 day_of_week 存在且基于 UTC std
                        day_of_week: std?.isValid() ? std.day() || 7 : null, // 0 (周日) -> 7
                    };

                    // 最后使用ensureFlightTimeConsistency确保时间一致性
                    const importedSta = sta?.isValid() ? sta.format("YYYY-MM-DDTHH:mm:ss") + "Z" : null;
                    tempFlight = applyRouteBlockTimeStaPolicy(tempFlight, minBlockTimeMinutes);
                    tempFlight = ensureFlightTimeConsistency(tempFlight);
                    if (importedSta && tempFlight.sta && importedSta !== tempFlight.sta) {
                        excelStaRewriteCount += 1;
                    }

                    // 【航段航路BH优化】将成功的航班添加到列表
                    successfulFlights.push(tempFlight);
                } // 结束 for 循环

                // 【航段航路BH优化】处理导入结果
                console.log(`[Excel导入] 导入完成 - 成功: ${successfulFlights.length}, 失败: ${failedLegs.length}`);

                // 处理失败的航班（缺少航路）
                if (failedLegs.length > 0) {
                    // 询问用户是否忽略错误并导入
                    const shouldIgnore = await new Promise((resolve) => {
                        Modal.confirm({
                            title: "Excel导入警告",
                            width: 600,
                            content: (
                                <div>
                                    <p style={{ marginBottom: 16, color: "#faad14" }}>{successfulFlights.length === 0 ? "所有航班因缺少可用航路而无法通过验证。" : `有 ${failedLegs.length} 个航班因缺少可用航路而无法通过验证。`}</p>
                                    <div style={{ maxHeight: 300, overflow: "auto", marginBottom: 16 }}>
                                        <h4>验证失败的航班：</h4>
                                        <ul style={{ paddingLeft: 20 }}>
                                            {failedLegs.map((leg, idx) => (
                                                <li key={idx} style={{ marginBottom: 8 }}>
                                                    <strong>{leg.flight_number}</strong> ({leg.leg})
                                                    <br />
                                                    <span style={{ color: "#ff4d4f", fontSize: 12 }}>原因: {leg.reason}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <p>是否忽略这些错误并强制导入？（导入后请手动补充航路信息）</p>
                                </div>
                            ),
                            okText: "忽略并导入",
                            cancelText: "取消导入", // 或者 "仅导入成功航班"
                            okButtonProps: { danger: true },
                            onOk: () => resolve(true),
                            onCancel: () => resolve(false),
                        });
                    });

                    if (shouldIgnore) {
                        // 用户选择忽略错误，将无效航班加入成功列表
                        successfulFlights.push(...invalidRouteFlights);
                        message.info(`已忽略验证错误，强制导入 ${invalidRouteFlights.length} 个航班`);
                    } else {
                        // 用户选择取消，如果也没有成功的航班，则直接返回
                        if (successfulFlights.length === 0) {
                            setLoading(false);
                            return;
                        }
                        // 如果有成功的航班，继续导入成功的航班（保持原有逻辑）
                        message.info("已跳过验证失败的航班");
                    }
                }

                // 如果没有成功导入任何航班（且用户未选择强制导入）
                if (successfulFlights.length === 0) {
                    setLoading(false);
                    return;
                }

                // 创建一个完全独立的航班列表副本，而不是合并现有列表
                // 这样可以避免状态管理中可能的引用问题
                const allFlights = [
                    ...flights.map((flight) => ({ ...flight })), // 复制现有航班
                    ...successfulFlights.map((flight) => ({
                        ...flight,
                        isMassUpdate: true, // 标记为批量更新
                    })), // 新导入的航班
                ];

                const keepPaperRotations = shouldKeepPaperRotations(schedule);
                const transportationDemands = state.parameters?.transportation_demands || [];
                const transportOrigins = keepPaperRotations ? new Set() : RotationService.getTransportOrigins(allFlights, transportationDemands);
                const flightsWithRotations = keepPaperRotations ? allFlights : RotationService.recalculateRotations(allFlights, transportOrigins);

                // 标记所有航班为一次批量/结构更新，这样上下文处理可以统一进行验证等后续操作
                const finalFlightsForUpdate = flightsWithRotations.map((flight) => ({
                    ...flight,
                    isMassUpdate: true, // 或者 isStructuralUpdate: true，取决于上下文如何处理
                }));

                // 使用batchUpdateFlights一次性更新包含新航班和更新后轮换的状态
                batchUpdateFlights(finalFlightsForUpdate);

                const importedFlightIds = successfulFlights.map((f) => f.id);
                // 确保高亮是在状态更新后执行
                setTimeout(() => {
                    highlightRows(importedFlightIds); // 高亮新导入的行
                }, 100); // 保留 setTimeout 确保 DOM 更新后再高亮

                // 导入完成提示
                message.success(`成功导入 ${successfulFlights.length} 个航班`);
                if (missingGroundTimeAirports.size > 0) {
                    message.warning(`以下到达站未配置最小过站时间，导入后已留空：${Array.from(missingGroundTimeAirports).join("、")}。请先到机场主数据维护最小过站时间`, 8);
                }
                if (excelStaRewriteCount > 0) {
                    message.info(`已按匹配轮档更新 ${excelStaRewriteCount} 班到达（起飞没动）`, 5);
                }

                // 导入后重新计算轮换可能是必要的 (已在上面合并处理，移除此独立调用)
                // recalculateRotations();

                setLoading(false); // 结束加载状态
            } catch (error) {
                console.error("处理导入数据时出错:", error);
                message.error("导入失败: " + (error.message || "未知错误"));
                setLoading(false); // 错误时也要结束加载状态
            }
        },
        [flights, schedule, state, batchUpdateFlights, highlightRows, message],
    );

    // 使用ref存储最新的导入函数引用
    const importFunctionRef = useRef(handleDataParsedFromImport);

    // 更新ref中的函数引用
    useEffect(() => {
        importFunctionRef.current = handleDataParsedFromImport;
    }, [handleDataParsedFromImport]);

    // 注册导入函数到context - 使用稳定的包装函数
    useEffect(() => {
        if (setImportFunction) {
            const stableImportFunction = (...args) => {
                return importFunctionRef.current(...args);
            };
            setImportFunction(stableImportFunction);
        }
    }, [setImportFunction]); // 只依赖setImportFunction，避免无限循环

    // 筛选逻辑函数
    const applyFilters = useCallback((flightList, filters) => {
        if (!filters || Object.keys(filters).length === 0) {
            return flightList;
        }

        return flightList.filter((flight) => {
            // 日期筛选
            if (filters.dateRange && filters.dateRange.length === 2) {
                if (!flight.flight_date) return false;

                const flightDate = dayjs(flight.flight_date);
                const [startDate, endDate] = filters.dateRange;

                // 确保 flightDate 是有效的 dayjs 对象
                if (!flightDate.isValid()) return false;

                // 确保 startDate 和 endDate 也是有效的 dayjs 对象
                const start = dayjs(startDate);
                const end = dayjs(endDate);
                if (!start.isValid() || !end.isValid()) return false;

                if (!flightDate.isBetween(start, end, "day", "[]")) {
                    return false;
                }
            }

            // 星期筛选
            if (filters.dayOfWeek && filters.dayOfWeek.length > 0) {
                let flightDayOfWeek = flight.day_of_week;

                // 如果没有 day_of_week，尝试从 std 计算
                if (!flightDayOfWeek && flight.std) {
                    const stdDate = dayjs(flight.std);
                    if (stdDate.isValid()) {
                        flightDayOfWeek = stdDate.day() || 7; // 0 (周日) -> 7
                    }
                }

                // 如果仍然没有有效的星期值，跳过此航班
                if (!flightDayOfWeek) return false;

                if (!filters.dayOfWeek.includes(flightDayOfWeek)) {
                    return false;
                }
            }

            // 航班号筛选
            if (filters.flightNumber && typeof filters.flightNumber === "string" && filters.flightNumber.trim()) {
                if (!flight.flight_number || typeof flight.flight_number !== "string") {
                    return false;
                }
                if (!flight.flight_number.toLowerCase().includes(filters.flightNumber.toLowerCase())) {
                    return false;
                }
            }

            // 出发机场筛选
            if (filters.departureAirport && Array.isArray(filters.departureAirport) && filters.departureAirport.length > 0) {
                if (!flight.departure_airport || !filters.departureAirport.includes(flight.departure_airport)) {
                    return false;
                }
            }

            // 到达机场筛选
            if (filters.arrivalAirport && Array.isArray(filters.arrivalAirport) && filters.arrivalAirport.length > 0) {
                if (!flight.arrival_airport || !filters.arrivalAirport.includes(flight.arrival_airport)) {
                    return false;
                }
            }

            // 地面时间范围筛选
            if (filters.groundTimeRange && filters.groundTimeRange.length === 2) {
                const groundTime = typeof flight.ground_time_minutes === "number" ? flight.ground_time_minutes : 0;
                const [minTime, maxTime] = filters.groundTimeRange;
                if (typeof minTime === "number" && typeof maxTime === "number") {
                    if (groundTime < minTime || groundTime > maxTime) {
                        return false;
                    }
                }
            }

            // 地面时间超出筛选
            if (filters.groundTimeExcess && filters.groundTimeExcess.length === 2) {
                const [minExcess, maxExcess] = filters.groundTimeExcess;

                // 只有当航班有地面时间时，此筛选才适用
                if (typeof flight.ground_time_minutes !== "number") {
                    return false; // 没有地面时间的航班不应通过此筛选
                }

                const groundTime = flight.ground_time_minutes;
                // 与表格展示同一口径：最小过站取机场主数据当前值。
                // 到达站没配时不能当成 0——那会把「过站配置缺失」算成「超出 0 分钟」。
                const minGroundTime = getAirportMinGroundTime(airportConstraints, flight.arrival_airport);
                if (minGroundTime === null) {
                    return false;
                }
                const excess = groundTime - minGroundTime;

                if (typeof minExcess === "number" && typeof maxExcess === "number") {
                    if (excess <= minExcess || excess >= maxExcess) {
                        return false;
                    }
                }
            }

            return true;
        });
    }, [airportConstraints]);

    // 处理筛选提交
    const handleFilterSubmit = (values) => {
        setActiveFilters(values);
        setFilterVisible(false);
    };

    // 清除筛选
    const handleClearFilters = () => {
        setActiveFilters({});
        filterForm.resetFields();
        setFilterVisible(false);
    };

    // 使用useMemo计算过滤后的航班
    const filteredFlights = useMemo(() => {
        const sortedFlights = TableService.filterAndSortFlights(flights);
        return applyFilters(sortedFlights, activeFilters);
    }, [flights, activeFilters, applyFilters]);

    // 初始化表格行选择 - 使用简单的数据源，与 ScheduleOfficialList 一致
    const { selectedRowKeys, selectedRows, tableRef, rowSelection, clearSelection, getIndexColumn, getRowProps } = useTableSelection({
        rowKey: "id",
        dataSource: filteredFlights, // 使用简单的过滤后数据源
        pagination: { current: 1, pageSize: 100 }, // 航班列表通常不分页
        mouseMoveThrottle: 100,
    });

    // 「重算选中」：完整列表连续校验 → 按基准规则只算选中段（须在勾选状态之后定义）
    const handleRecalculateSelected = useCallback(() => {
        if (autoCalculateInProgress) return;
        if (!selectedRowKeys || selectedRowKeys.length === 0) return;

        const orderedFlights = TableService.filterAndSortFlights(flights);
        const resolution = SelectedRangeRecalcService.resolveSelectedContinuousRange(orderedFlights, selectedRowKeys);

        if (!resolution.continuous) {
            message.warning(resolution.message || "请勾选连续的一段航班");
            return;
        }

        if (!resolution.calculable) {
            message.info(resolution.message || "选中范围内没有可重算的航班");
            return;
        }

        handleAutoCalculate(resolution.baseIndex, {
            endIndex: resolution.endIndex,
            flightsOverride: orderedFlights,
            rangeMeta: {
                rangeStartIndex: resolution.rangeStartIndex,
                rangeEndIndex: resolution.rangeEndIndex,
                // 门禁「段内/段外」文案用勾选 id，不依赖试算后索引切片
                selectedIds: [...selectedRowKeys],
            },
        });
    }, [autoCalculateInProgress, selectedRowKeys, flights, handleAutoCalculate, message]);

    // 拖拽排序
    const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
    const sortableItems = useMemo(() => filteredFlights.map((f) => f.id), [filteredFlights]);

    const handleDragEnd = useCallback(
        (event) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;

            const oldIndex = filteredFlights.findIndex((f) => f.id === active.id);
            const newIndex = filteredFlights.findIndex((f) => f.id === over.id);
            if (oldIndex === -1 || newIndex === -1) return;

            const reordered = arrayMove(filteredFlights, oldIndex, newIndex);
            const orderMap = new Map();
            reordered.forEach((flight, idx) => {
                orderMap.set(flight.id, idx + 1);
            });

            const updatedFlights = flights.map((flight) => (orderMap.has(flight.id) ? { ...flight, display_order: orderMap.get(flight.id) } : flight));
            setFlights(updatedFlights);
        },
        [filteredFlights, flights, setFlights],
    );

    // 表格列定义
    const columns = [
        {
            title: "",
            dataIndex: "_drag",
            key: "_drag",
            width: 30,
            align: "center",
            render: () => <DragHandle />,
        },
        // 直接使用 useTableSelection 提供的 getIndexColumn，与 ScheduleOfficialList 一致
        getIndexColumn("No.", 60, { align: "center" }),
        {
            title: <span style={{ whiteSpace: "nowrap" }}>Aircraft Reg.</span>,
            dataIndex: "aircraft_reg",
            key: "aircraft_reg",
            align: "center",
            width: 100,
            render: (text, record) => {
                // 如果有导入批次标记，显示上标
                if (record._importBatch) {
                    // 根据批次号选择不同颜色，循环使用 6 种颜色
                    const colors = ["#1890ff", "#52c41a", "#faad14", "#eb2f96", "#722ed1", "#13c2c2"];
                    const colorIndex = (record._importBatch - 1) % colors.length;
                    return (
                        <span>
                            {text}
                            <sup style={{ color: colors[colorIndex], fontWeight: "bold", marginLeft: 2 }}>#{record._importBatch}</sup>
                        </span>
                    );
                }
                return text;
            },
        },
        {
            title: <span style={{ whiteSpace: "nowrap" }}>Date</span>,
            dataIndex: "flight_date",
            key: "flight_date",
            align: "center",
            width: 60,
            render: (text, record) => {
                // 使用 FlightDateDisplay 组件，修正参数名
                return <FlightDateDisplay etdOrStd={record.std} departureAirportCode={record.departure_airport} originalFlightDateForFallback={record.flight_date} />;
            },
        },
        {
            title: (
                <span style={{ whiteSpace: "nowrap" }}>
                    <Tooltip title="Day">D</Tooltip>
                </span>
            ),
            dataIndex: "day_of_week",
            key: "day_of_week",
            align: "center",
            width: 30,
            render: (text, record) => {
                // 使用时区感知的星期几计算
                return getTimezoneAwareDayOfWeek(record);
            },
        },
        {
            title: <span style={{ whiteSpace: "nowrap" }}>Flight No.</span>,
            dataIndex: "flight_number",
            key: "flight_number",
            align: "center",
            width: 100,
            render: (text, record) => {
                const violations = getFlightViolations(record.id);
                const pairViolations = getFlightPairViolations(record.id);
                const errorViolations = violations.filter((item) => item.severity === "error");
                const warningViolations = violations.filter((item) => item.severity === "warning");
                const formatAvailabilityTime = (value, item) => {
                    if (!value) return "?";
                    const airportCode = item.airportCode || record.departure_airport;
                    const formatted = formatFlightTime(
                        value,
                        airportCode,
                        timeZone,
                        airportsMap,
                        null,
                        "YYYY-MM-DD HH:mm",
                    );
                    return `${formatted.time} ${formatted.label}`.trim();
                };
                const buildViolationTooltip = (items) => items
                    .flatMap((item) => {
                        const messages = item.messages || [item.message];
                        if (!String(item.violationType || "").startsWith("aircraft_unavailable")) {
                            return messages;
                        }
                        const windowRange = `${formatAvailabilityTime(item.windowStart, item)} - ${item.windowEnd ? formatAvailabilityTime(item.windowEnd, item) : "无限期"}`;
                        const relation = item.violationType === "aircraft_unavailable"
                            ? `重叠范围 ${formatAvailabilityTime(item.overlapStart, item)} - ${formatAvailabilityTime(item.overlapEnd, item)}`
                            : `相距 ${item.gapMinutes} 分钟（${item.nearSide === "before_start" ? "窗口开始前" : "窗口结束后"}）`;
                        return [
                            ...messages,
                            `飞机 ${item.aircraftReg || "?"}；${item.windowTitle || item.windowType || "不可用窗口"}${item.airportCode ? ` @ ${item.airportCode}` : ""}`,
                            `窗口 ${windowRange}；${relation}`,
                        ];
                    })
                    .filter(Boolean)
                    .join("\n");

                // 检查是否有时间调整
                const hasTimeAdjustments = record.time_adjustments && record.time_adjustments.length > 0;

                // 公共内容提取
                const flightNumberContent = (
                    <Space>
                        {text}
                        {errorViolations.length > 0 && (
                            <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{buildViolationTooltip(errorViolations)}</span>}>
                                <WarningOutlined style={{ color: "#f5222d" }} />
                            </Tooltip>
                        )}
                        {warningViolations.length > 0 && (
                            <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{buildViolationTooltip(warningViolations)}</span>}>
                                <WarningOutlined style={{ color: "#faad14" }} />
                            </Tooltip>
                        )}
                        {pairViolations && pairViolations.length > 0 && (
                            <Tooltip title={pairViolations[0].message}>
                                <ClockCircleOutlined style={{ color: "#faad14" }} />
                            </Tooltip>
                        )}
                        {hasTimeAdjustments && (
                            <Tag color="cyan" style={{ marginLeft: 2, fontWeight: "bold" }}>
                                调整
                            </Tag>
                        )}
                    </Space>
                );

                return hasTimeAdjustments ? (
                    <Tooltip
                        title={DisplayService.renderTimeAdjustmentsDetail(record.time_adjustments)}
                        placement="top"
                        color="#fff"
                        styles={{
                            root: { maxWidth: "500px" },
                            body: { color: "rgba(0, 0, 0, 0.85)" },
                        }}
                    >
                        {flightNumberContent}
                    </Tooltip>
                ) : (
                    flightNumberContent
                );
            },
        },
        {
            title: <span style={{ whiteSpace: "nowrap" }}>DEP</span>,
            dataIndex: "departure_airport",
            key: "departure_airport",
            align: "center",
            width: 60,
            render: (text, record) => {
                const airport = airportsMap[text];
                const continuityViolation = validationResults && validationResults.find((v) => v.flightId === record.id && v.violationType === "flight_continuity");

                return (
                    <span>
                        {continuityViolation && (
                            <Tooltip title={continuityViolation.messages[0]}>
                                <Tag color="orange" style={{ marginRight: 4 }}>
                                    <WarningOutlined />
                                </Tag>
                            </Tooltip>
                        )}
                        <Tooltip title={airport ? airport.name : text}>{text}</Tooltip>
                    </span>
                );
            },
        },
        {
            title: <span style={{ whiteSpace: "nowrap" }}>ARR</span>,
            dataIndex: "arrival_airport",
            key: "arrival_airport",
            align: "center",
            width: 60,
            render: (text) => {
                const airport = airportsMap[text];
                if (airport) {
                    return <Tooltip title={airport.name}>{text}</Tooltip>;
                }
                return text;
            },
        },
        {
            title: (
                <Space>
                    <span style={{ whiteSpace: "nowrap" }}>Route</span>
                    <InlineHelpButton contentPath="help/2025-08-22_flight-route-binding.md" title="航段选择与航路绑定功能说明" tooltip="查看航段选择和自动航路绑定的使用说明" />
                </Space>
            ),
            dataIndex: "flight_route_id",
            key: "flight_route_id",
            align: "center",
            width: 80,
            render: (routeId, record) => {
                // 【航段航路BH优化】强化无航路的警告显示
                if (!routeId) {
                    return (
                        <Tooltip title="该航班缺少航路关联，请编辑航班并选择航路">
                            <Tag color="red" style={{ fontSize: "11px" }}>
                                缺失
                            </Tag>
                        </Tooltip>
                    );
                }

                // 显示航路名称
                const routeName = record.flight_route_name;
                // 「非默认」标记只在还绑着导入时那条航路时显示（改绑后自动消失，不追着新绑定）
                const nonDefaultFlag = nonDefaultRouteFlags[record.id];
                const showNonDefaultFlag = Boolean(nonDefaultFlag) && String(nonDefaultFlag.route_id) === String(routeId);
                if (routeName) {
                    return (
                        <div style={{ lineHeight: 1.4 }}>
                            <Tooltip
                                title={
                                    <div>
                                        <div>
                                            <strong>航路名称:</strong> {routeName}
                                        </div>
                                        <div>
                                            <strong>航路ID:</strong> {routeId}
                                        </div>
                                        {record.flight_route_block_time_minutes && (
                                            <div>
                                                <strong>轮档时间:</strong> {formatDuration(record.flight_route_block_time_minutes, "h_m")}
                                            </div>
                                        )}
                                    </div>
                                }
                            >
                                <span style={{ fontSize: "12px", color: "#1890ff", cursor: "pointer" }}>{routeName.length > 8 ? `${routeName.substring(0, 8)}...` : routeName}</span>
                            </Tooltip>
                            {showNonDefaultFlag && (
                                <Tooltip
                                    title={
                                        <div>
                                            <div>
                                                <strong>与当前默认不一致</strong>
                                            </div>
                                            <div>
                                                绑的航路：{nonDefaultFlag.route_name || nonDefaultFlag.route_id}（{nonDefaultFlag.block_time_minutes ? formatDuration(nonDefaultFlag.block_time_minutes, "h_m") : "无有效轮档"}）
                                            </div>
                                            <div>
                                                当前默认：{nonDefaultFlag.default_route_name || nonDefaultFlag.default_route_id}（{nonDefaultFlag.default_block_time_minutes ? formatDuration(nonDefaultFlag.default_block_time_minutes, "h_m") : "无有效轮档"}）
                                            </div>
                                            <div>点「刷新基础数据」可回归默认</div>
                                        </div>
                                    }
                                >
                                    <Tag color="orange" style={{ fontSize: "11px", marginTop: 2, cursor: "help" }}>
                                        非默认
                                    </Tag>
                                </Tooltip>
                            )}
                        </div>
                    );
                }

                // 如果没有航路名称，显示ID的后4位
                return (
                    <div style={{ lineHeight: 1.4 }}>
                        <Tooltip title={`航路ID: ${routeId}`}>
                            <span style={{ fontSize: "12px", color: "#1890ff" }}>{routeId.toString().slice(-4)}</span>
                        </Tooltip>
                        {showNonDefaultFlag && (
                            <Tooltip title="与当前默认不一致，点「刷新基础数据」可回归默认">
                                <Tag color="orange" style={{ fontSize: "11px", marginTop: 2, cursor: "help" }}>
                                    非默认
                                </Tag>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
        {
            title: (
                <Tooltip title="Standard Departure Time">
                    <span style={{ whiteSpace: "nowrap" }}>STD</span>
                </Tooltip>
            ),
            dataIndex: "std",
            key: "std",
            align: "center",
            width: 80,
            render: (text, record) => {
                // 检查宵禁违规和警告
                const violations = validationResults.filter((result) => result.flightId === record.id && result.violationTypes && (result.violationTypes.includes("departure_curfew") || result.violationTypes.includes("departure_curfew_warning") || result.violationTypes.includes("departure_curfew_notice")));

                return (
                    <span style={{ position: "relative" }}>
                        <FlightTimeDisplay utcTime={text} airportCode={record.departure_airport} airportsMap={airportsMap} />
                        {DisplayService.renderCurfewViolationTag(violations, "departure_curfew")}
                    </span>
                );
            },
        },
        {
            title: (
                <Tooltip title="Standard Arrival Time">
                    <span style={{ whiteSpace: "nowrap" }}>STA</span>
                </Tooltip>
            ),
            dataIndex: "sta",
            key: "sta",
            align: "center",
            width: 80,
            render: (text, record) => {
                // 检查宵禁违规和警告
                const violations = validationResults.filter((result) => result.flightId === record.id && result.violationTypes && (result.violationTypes.includes("arrival_curfew") || result.violationTypes.includes("arrival_curfew_warning") || result.violationTypes.includes("arrival_curfew_notice")));

                return (
                    <span style={{ position: "relative" }}>
                        <FlightTimeDisplay utcTime={text} airportCode={record.arrival_airport} airportsMap={airportsMap} />
                        {DisplayService.renderCurfewViolationTag(violations, "arrival_curfew")}
                        {DisplayService.renderStaFollowHint(record)}
                    </span>
                );
            },
        },
        {
            title: (
                <Tooltip title="Rotation">
                    <span style={{ whiteSpace: "nowrap" }}>ROT</span>
                </Tooltip>
            ),
            dataIndex: "rotation_no",
            key: "rotation_no",
            width: 40,
            align: "center",
            render: (text, record) => {
                if (shouldKeepPaperRotations(schedule)) {
                    return <RotationVisualizer flight={record} allFlights={filteredFlights} />;
                }
                const transportationDemands = state.parameters?.transportation_demands || [];
                const transportOrigins = RotationService.getTransportOrigins(flights, transportationDemands);

                return DisplayService.renderRotationCell(record, filteredFlights, transportOrigins);
            },
        },
        {
            title: <span style={{ whiteSpace: "nowrap" }}>Block Time</span>,
            dataIndex: "block_time_minutes",
            align: "center",
            width: 80,
            render: (blockTime, record) => <span className="text-secondary">{DisplayService.renderBlockTime(record)}</span>,
        },
        {
            title: <span style={{ whiteSpace: "nowrap" }}>STC</span>,
            dataIndex: "stc_type",
            key: "stc_type",
            align: "center",
            width: 80,
        },
        {
            title: <span style={{ whiteSpace: "nowrap" }}>Ground Time</span>,
            dataIndex: "ground_time_minutes",
            key: "ground_time_minutes",
            align: "center",
            width: 80,
            render: (actualGroundTime, record) => {
                // 1. 实际地面时间直接从 record.ground_time_minutes 获取 (已由 Context 计算好)
                // 2. 最小过站只认到达机场主数据的当前值（含 0 分钟），
                //    不读航班上存着的旧数字——那可能是以前写下的假 1 小时
                const minGroundTime = getAirportMinGroundTime(airportConstraints, record.arrival_airport);

                // 3. 地面时间违规检查是针对 *当前* 航班与下一个航班之间的；
                //    归属已由 getFlightPairViolations 限定为「本班作为前一班」的条目
                const pairViolations = getFlightPairViolations(record.id);
                const missingConfigViolation = pairViolations?.find((v) => v.violationType === "min_ground_time_missing" || v.type === "min_ground_time_missing");
                if (missingConfigViolation) {
                    // 缺配置不是“过站不足”，单独标出，避免被当成正常或被误读成 1 小时要求
                    return (
                        <Tooltip title={missingConfigViolation.message}>
                            <span style={{ color: "#ff4d4f", fontSize: 12, whiteSpace: "nowrap" }}>过站配置缺失</span>
                        </Tooltip>
                    );
                }
                const groundTimeViolation = pairViolations?.find((v) => v.violationType === "ground_time" || v.type === "ground_time");

                // 4. 调用 DisplayService.renderGroundTime
                return DisplayService.renderGroundTime(
                    actualGroundTime, // 直接使用 record.ground_time_minutes
                    minGroundTime,
                    groundTimeViolation,
                );
            },
        },
        {
            title: <span style={{ whiteSpace: "nowrap" }}>Remarks</span>,
            dataIndex: "remarks",
            key: "remarks",
            align: "center",
            ellipsis: true,
            width: 100,
        },
        {
            title: <span style={{ whiteSpace: "nowrap" }}>Actions</span>,
            key: "action",
            align: "center",
            fixed: "right",
            width: 300,
            render: (_, record, index) => (
                <Space size="small">
                    <Button
                        type="text"
                        icon={<InsertRowRightOutlined />}
                        onClick={() => {
                            const realIndex = filteredFlights.findIndex((f) => f.id === record.id);
                            if (realIndex >= 0) {
                                handleAddLeg(realIndex);
                            }
                        }}
                        title="在此航班后插入航段"
                    >
                        添加
                    </Button>
                    <Space.Compact>
                        <Button
                            type="text"
                            icon={<CalculatorOutlined />}
                            onClick={() => {
                                // 用完整列表定位起点：计算作用于完整 flights 列表，
                                // 过滤态下用过滤后的行号会算错起点
                                const realIndex = flights.findIndex((f) => f.id === record.id);
                                if (realIndex >= 0) {
                                    handleAutoCalculate(realIndex);
                                }
                            }}
                            title="计算此航班后的所有航班时间"
                        >
                            重算
                        </Button>
                    </Space.Compact>
                    <Button
                        type="link"
                        onClick={() => {
                            const realIndex = filteredFlights.findIndex((f) => f.id === record.id);
                            handleEdit(record, realIndex);
                        }}
                        title="编辑"
                    >
                        编辑
                    </Button>
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} title="删除" />
                </Space>
            ),
        },
    ].filter(Boolean); // 移除可能的 null/undefined 值

    // 自定义行属性函数 - 只允许序号列选中，移除整行点击
    const getCustomRowProps = useCallback(
        (record, index) => {
            const originalProps = getRowProps(record, index);
            // 移除原始的 onClick 事件，只保留其他属性
            const { onClick, ...restProps } = originalProps;
            return {
                ...restProps,
                // 不添加 onClick 事件，这样点击行的其他部分不会触发选中
            };
        },
        [getRowProps],
    );

    // 重新计算所有航班的轮换信息
    const recalculateRotations = () => {
        if (!flights || flights.length === 0) {
            return;
        }
        if (shouldKeepPaperRotations(schedule)) {
            return;
        }

        try {
            // 获取运输需求数据，用于识别轮换起始点
            const transportationDemands = state.parameters?.transportation_demands || [];
            const transportOrigins = RotationService.getTransportOrigins(flights, transportationDemands);
            const updatedFlights = RotationService.recalculateRotations(flights, transportOrigins);

            // === 关键修复：检测轮换信息是否真的发生变化 ===
            // 只有当轮换相关字段真的变化时才更新状态，避免初始化时意外触发 isDirty
            const ROTATION_FIELDS = ["rotation_no", "rotation_index", "rotation_leg_index", "is_rotation_complete"];

            let hasRotationChange = false;

            for (let i = 0; i < flights.length; i++) {
                const original = flights[i];
                const updated = updatedFlights[i];

                // 如果找不到对应航班，说明有结构变化
                if (!updated) {
                    hasRotationChange = true;
                    break;
                }

                // 比较轮换相关字段
                for (const field of ROTATION_FIELDS) {
                    if (original[field] !== updated[field]) {
                        hasRotationChange = true;
                        break;
                    }
                }

                if (hasRotationChange) break;
            }

            // 如果轮换信息没有变化，跳过更新
            if (!hasRotationChange) {
                return;
            }
            // === 修复结束 ===

            // 更新航班数据 - 使用批量更新函数，提高性能
            // 标记此次更新为结构化更新，而非内容更新
            const massUpdateFlights = updatedFlights.map((flight) => ({
                ...flight,
                isStructuralUpdate: true, // 添加标记，表示是结构更新，不需要重新验证每个航班约束
            }));

            // 使用优化的批量更新函数
            // 轮换信息是派生数据，其自动重算不应触发 isDirty，所以使用 silent 模式
            batchUpdateFlights(massUpdateFlights, { silent: true });
        } catch (error) {
            console.error("计算轮换失败:", error);
            message.error("计算轮换失败");
        }
    };

    // 创建一个只包含轮换计算所需关键信息的依赖项
    const rotationDependencies = useMemo(() => {
        if (!flights || flights.length === 0) {
            return "[]"; // 返回一个稳定的空值字符串
        }
        // 提取关键信息：id, aircraft_reg, std, departure_airport, arrival_airport
        const criticalFlightData = flights
            .map((f) => ({
                id: f.id, // id 变化意味着增删
                aircraft_reg: f.aircraft_reg,
                std: f.std, // 用于排序
                departure_airport: f.departure_airport,
                arrival_airport: f.arrival_airport,
            }))
            .sort((a, b) => {
                // 确保排序一致性，即使原始 flights 顺序变化
                if (a.aircraft_reg !== b.aircraft_reg) {
                    return (a.aircraft_reg || "").localeCompare(b.aircraft_reg || "");
                }
                return dayjs(a.std).valueOf() - dayjs(b.std).valueOf();
            });
        // 将关键数据和运输需求字符串化以创建稳定依赖
        return JSON.stringify({
            flights: criticalFlightData,
            demands: state.parameters?.transportation_demands || [],
        });
    }, [flights, state.parameters?.transportation_demands]);

    // 使用新的依赖项触发 recalculateRotations
    useEffect(() => {
        // 只有当依赖项实际生成（且航班列表不为空）时才计算
        // 初始加载或依赖项变化时执行
        // 直接调用，recalculateRotations内部会处理 flights 为空的情况
        recalculateRotations();
    }, [rotationDependencies]); // 依赖于稳定化的关键信息

    // 自定义空状态显示
    const customEmpty = () => (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span>暂无航班数据，请点击"添加航段"按钮添加</span>}>
            <Space.Compact>
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => handleAddLeg()}>
                    添加航段
                </Button>
                <Dropdown
                    menu={{
                        items: [
                            {
                                key: "add-first",
                                label: "在首行添加航段",
                                icon: <PlusOutlined />,
                                onClick: () => handleAddLeg(-1),
                            },
                            {
                                key: "add-last",
                                label: "在尾行添加航段",
                                icon: <PlusOutlined />,
                                onClick: () => handleAddLeg(),
                            },
                        ],
                    }}
                    placement="bottomLeft"
                    trigger={["click"]}
                >
                    <Button type="primary" size="small" icon={<DownOutlined />} />
                </Dropdown>
            </Space.Compact>
        </Empty>
    );

    // 获取机场选项
    const getAirportOptions = useMemo(() => {
        const airports = new Set();
        flights.forEach((flight) => {
            if (flight.departure_airport) airports.add(flight.departure_airport);
            if (flight.arrival_airport) airports.add(flight.arrival_airport);
        });
        return Array.from(airports)
            .sort()
            .map((code) => ({
                label: airportsMap[code] ? `${code}${airportsMap[code].icao_code ? ` - ${airportsMap[code].icao_code}` : ""}${airportsMap[code].airport_name ? ` (${airportsMap[code].airport_name})` : ""}` : code,
                value: code,
            }));
    }, [flights, airportsMap]);

    // 获取地面时间范围
    const getGroundTimeRange = useMemo(() => {
        const groundTimes = flights.map((f) => f.ground_time_minutes).filter((t) => typeof t === "number" && Number.isFinite(t));
        if (groundTimes.length === 0) return [0, 300];
        return [Math.min(...groundTimes), Math.max(...groundTimes)];
    }, [flights]);

    // 获取地面时间超出范围：与筛选同一口径，只认机场当前最小过站；缺配置的班不进滑条
    const getGroundTimeExcessRange = useMemo(() => {
        const excesses = flights.flatMap((f) => {
            if (typeof f.ground_time_minutes !== "number" || !Number.isFinite(f.ground_time_minutes)) {
                return [];
            }
            const minGroundTime = getAirportMinGroundTime(airportConstraints, f.arrival_airport);
            if (minGroundTime === null) {
                return [];
            }
            return [f.ground_time_minutes - minGroundTime];
        });
        if (excesses.length === 0) return [-60, 120]; // 如果没有航班有地面时间，提供一个合理的默认范围
        return [Math.min(...excesses), Math.max(...excesses)];
    }, [flights, airportConstraints]);

    // 渲染筛选面板
    const renderFilterPanel = () => (
        <Collapse activeKey={filterVisible ? ["filter"] : []} onChange={(keys) => setFilterVisible(keys.includes("filter"))} size="small" ghost>
            <Collapse.Panel
                key="filter"
                header={
                    <Space>
                        <FilterOutlined />
                        筛选航班
                        <InlineHelpButton contentPath="help/2025-7-23_flight-schedule-filter.md" title="航班筛选功能说明" tooltip="查看筛选功能使用说明" />
                        {getActiveFilterCount > 0 && (
                            <Tag color="blue" size="small">
                                {getActiveFilterCount} 个筛选条件
                            </Tag>
                        )}
                    </Space>
                }
                extra={
                    getActiveFilterCount > 0 && (
                        <Button
                            type="text"
                            size="small"
                            icon={<ClearOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClearFilters();
                            }}
                        >
                            清除
                        </Button>
                    )
                }
            >
                <Form form={filterForm} layout="vertical" onFinish={handleFilterSubmit} size="small">
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
                        <Form.Item label="日期范围" name="dateRange">
                            <DatePicker.RangePicker style={{ width: "100%" }} format="YYYY-MM-DD" placeholder={["开始日期", "结束日期"]} />
                        </Form.Item>

                        <Form.Item label="星期" name="dayOfWeek">
                            <Select
                                mode="multiple"
                                placeholder="选择星期"
                                options={[
                                    { label: "1", value: 1 },
                                    { label: "2", value: 2 },
                                    { label: "3", value: 3 },
                                    { label: "4", value: 4 },
                                    { label: "5", value: 5 },
                                    { label: "6", value: 6 },
                                    { label: "7", value: 7 },
                                ]}
                            />
                        </Form.Item>

                        <Form.Item label="航班号" name="flightNumber">
                            <Input placeholder="输入航班号" allowClear />
                        </Form.Item>

                        <Form.Item label="出发机场" name="departureAirport">
                            <Select mode="multiple" placeholder="选择出发机场" options={getAirportOptions} showSearch filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())} />
                        </Form.Item>

                        <Form.Item label="到达机场" name="arrivalAirport">
                            <Select mode="multiple" placeholder="选择到达机场" options={getAirportOptions} showSearch filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())} />
                        </Form.Item>

                        <Form.Item label="地面时间范围(分钟)" name="groundTimeRange">
                            <Slider
                                range
                                min={getGroundTimeRange[0]}
                                max={getGroundTimeRange[1]}
                                marks={{
                                    [getGroundTimeRange[0]]: `${getGroundTimeRange[0]}`,
                                    [getGroundTimeRange[1]]: `${getGroundTimeRange[1]}`,
                                }}
                            />
                        </Form.Item>

                        <Form.Item label="地面时间超出(分钟)" name="groundTimeExcess">
                            <Slider
                                range
                                min={getGroundTimeExcessRange[0]}
                                max={getGroundTimeExcessRange[1]}
                                marks={{
                                    [getGroundTimeExcessRange[0]]: `${getGroundTimeExcessRange[0]}`,
                                    [getGroundTimeExcessRange[1]]: `${getGroundTimeExcessRange[1]}`,
                                }}
                            />
                        </Form.Item>
                    </div>

                    <div style={{ textAlign: "right", marginTop: "16px" }}>
                        <Space>
                            <Button onClick={handleClearFilters}>清除</Button>
                            <Button type="primary" htmlType="submit">
                                应用筛选
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Collapse.Panel>
        </Collapse>
    );

    // 批量删除选中的航班
    const handleBatchDelete = useCallback(() => {
        if (selectedRowKeys.length === 0) {
            message.warning("请先选择要删除的航班");
            return;
        }

        Modal.confirm({
            title: "确认删除",
            content: `确定要删除选中的 ${selectedRowKeys.length} 个航班吗？此操作不可撤销。`,
            okText: "确定删除",
            cancelText: "取消",
            okType: "danger",
            onOk: () => {
                // 删除选中的航班
                const newFlights = flights.filter((flight) => !selectedRowKeys.includes(flight.id));
                setFlights(newFlights);
                clearSelection(); // 清空选择
                message.success(`成功删除 ${selectedRowKeys.length} 个航班`);
            },
        });
    }, [selectedRowKeys, flights, setFlights, clearSelection, message]);

    // 全选/取消全选
    const handleSelectAll = useCallback(() => {
        if (selectedRowKeys.length === filteredFlights.length && filteredFlights.length > 0) {
            // 当前已全选，执行取消全选
            clearSelection();
        } else {
            // 执行全选 - 由于 useTableSelection 没有直接的全选方法，
            // 我们需要通过 Ant Design Table 的 rowSelection.onSelectAll 来实现
            // 这里我们模拟全选操作
            const allKeys = filteredFlights.map((flight) => flight.id);
            const allRows = filteredFlights;

            // 直接调用 rowSelection 的 onChange 方法
            if (rowSelection && rowSelection.onChange) {
                rowSelection.onChange(allKeys, allRows);
            }
        }
    }, [selectedRowKeys, filteredFlights, clearSelection, rowSelection]);

    // 在删除确认按钮旁添加导入按钮
    const renderHeaderButtons = () => (
        <Space>
            <Space.Compact>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAddLeg()}>
                    添加航段
                </Button>
                <Dropdown
                    menu={{
                        items: [
                            {
                                key: "add-first",
                                label: "在首行添加航段",
                                icon: <PlusOutlined />,
                                onClick: () => handleAddLeg(-1), // -1 表示在首行添加
                            },
                            {
                                key: "add-last",
                                label: "在尾行添加航段",
                                icon: <PlusOutlined />,
                                onClick: () => handleAddLeg(), // 默认在尾行添加
                            },
                        ],
                    }}
                    placement="bottomLeft"
                    trigger={["click"]}
                >
                    <Button type="primary" icon={<DownOutlined />} />
                </Dropdown>
            </Space.Compact>

            {/* 全选按钮 */}
            <Button icon={selectedRowKeys.length === filteredFlights.length && filteredFlights.length > 0 ? <CheckOutlined /> : <SelectOutlined />} onClick={handleSelectAll} disabled={filteredFlights.length === 0}>
                {selectedRowKeys.length === filteredFlights.length && filteredFlights.length > 0 ? "取消全选" : "全选"}
            </Button>

            <div style={{ position: "relative" }}>
                <Button icon={<ImportOutlined />} className="import-dropdown-trigger">
                    导入航班
                </Button>
                <div style={{ display: "none" }} className="import-dropdown">
                    {/* 这里需要区分是计划还是执行航班，计划原来只代表 draft 状态的，现在改为所有状态 */}
                    <Button type="text" block onClick={() => handleOpenImportModal("schedule")}>
                        从其他计划导入
                    </Button>
                    <Button type="text" block onClick={() => handleOpenImportModal("operation")}>
                        从执行航班导入
                    </Button>
                    <Button type="text" block onClick={() => setV2TrackImportVisible(true)}>
                        从 V2 编辑轨导入
                    </Button>
                    <Button type="text" block onClick={() => setImportFromTextVisible(true)}>
                        <FileTextOutlined /> 从文件/文本导入
                    </Button>
                </div>
            </div>

            <Button
                icon={<SettingOutlined />}
                onClick={() => {
                    setCalcModalVisible(true);
                    setCalcConfig({ ...calcConfig, startIndex: 0 });
                }}
            >
                设置
            </Button>

            <Popconfirm title="确定要清空所有航班吗?" onConfirm={handleClearAllFlights} okText="确定" cancelText="取消">
                <Button danger icon={<DeleteOutlined />}>
                    清空航班
                </Button>
            </Popconfirm>
        </Space>
    );

    return (
        <div className="flight-schedule-container">
            <Card title="航班编排" className="flight-schedule-section" extra={renderHeaderButtons()}>
                {shouldKeepPaperRotations(schedule) ? <Alert type="info" showIcon message="本源计划认纸面轮班，不按运输需求重算" style={{ marginBottom: 12 }} /> : null}
                {(aircraftAvailability?.loading || aircraftAvailability?.fetching) && (
                    <Alert
                        type="info"
                        showIcon
                        message="正在加载飞机可用性数据"
                        style={{ marginBottom: 12 }}
                    />
                )}
                {aircraftAvailability?.error && (
                    <Alert
                        type="warning"
                        showIcon
                        message="飞机可用性校验未完成"
                        description={aircraftAvailability.error.message || "读取飞机可用性数据失败"}
                        style={{ marginBottom: 12 }}
                    />
                )}
                {!aircraftAvailability?.error && aircraftAvailability?.stale && (
                    <Alert
                        type="warning"
                        showIcon
                        message="飞机可用性数据可能已过期，请刷新基础数据"
                        style={{ marginBottom: 12 }}
                    />
                )}
                {/* 筛选面板 */}
                {renderFilterPanel()}
                {/* 批量操作工具栏 - fixed 悬浮，列表滚动时仍可操作 */}
                {selectedRowKeys.length > 0 && (
                    <div className="batch-operations-info">
                        <span style={{ marginRight: 8, fontSize: "12px" }}>
                            已选择 <span style={{ fontWeight: "bold" }}>{selectedRowKeys.length}</span> 个航班
                        </span>
                        <Divider type="vertical" style={{ margin: "0 4px" }} />
                        <Button size="small" type="link" onClick={clearSelection} style={{ padding: "0 4px" }}>
                            取消选择
                        </Button>
                        <Divider type="vertical" style={{ margin: "0 4px" }} />
                        <Button size="small" type="primary" ghost icon={<CopyOutlined />} onClick={() => setCopyModalVisible(true)} style={{ fontSize: "12px" }}>
                            复制航班
                        </Button>
                        <Divider type="vertical" style={{ margin: "0 4px" }} />
                        <Button size="small" danger ghost icon={<DeleteOutlined />} onClick={handleBatchDelete} style={{ fontSize: "12px" }}>
                            批量删除
                        </Button>
                        <Divider type="vertical" style={{ margin: "0 4px" }} />
                        <Button
                            size="small"
                            type="primary"
                            ghost
                            icon={<CalculatorOutlined />}
                            onClick={handleRecalculateSelected}
                            disabled={autoCalculateInProgress}
                            style={{ fontSize: "12px" }}
                        >
                            重算选中
                        </Button>
                    </div>
                )}

                {filteredFlights && filteredFlights.length > 0 ? (
                    <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
                            <Table
                                ref={tableRef}
                                rowKey="id"
                                rowSelection={{
                                    ...rowSelection,
                                    // 隐藏复选框，只保留序号点击功能
                                    getCheckboxProps: () => ({
                                        style: { display: "none" },
                                    }),
                                }}
                                components={{ body: { row: DraggableRow } }}
                                columns={columns}
                                dataSource={filteredFlights}
                                pagination={false}
                                scroll={{ x: 1300 }}
                                size="small"
                                loading={loading}
                                rowClassName={(record) => {
                                    const transportationDemands = state.parameters?.transportation_demands || [];
                                    const transportOrigins = shouldKeepPaperRotations(schedule)
                                        ? new Set()
                                        : RotationService.getTransportOrigins(flights, transportationDemands);
                                    let baseClass = DisplayService.getRowClassName(record, transportOrigins);
                                    // 添加选中行样式
                                    if (selectedRowKeys.includes(record.id)) {
                                        baseClass += " row-selected";
                                    }
                                    // 添加高亮行样式
                                    if (highlightedRowIds.has(record.id)) {
                                        baseClass += " row-highlighted";
                                    }
                                    return baseClass;
                                }}
                                onRow={getCustomRowProps}
                                sticky={{ offsetHeader: 0 }}
                            />
                        </SortableContext>
                    </DndContext>
                ) : (
                    <div className="custom-empty-container">
                        {loading ? (
                            <div style={{ textAlign: "center" }}>
                                <div style={{ marginBottom: 16 }}>正在加载数据...</div>
                            </div>
                        ) : (
                            customEmpty()
                        )}
                    </div>
                )}
            </Card>

            {/* 航段选择器 */}
            {legSelectorVisible && <LegSelector visible={legSelectorVisible} onCancel={() => setLegSelectorVisible(false)} onSelect={handleLegSelected} prevFlight={selectedRowIndex !== null && flights.length > 0 ? flights[selectedRowIndex] : null} schedule={schedule} insertIndex={selectedRowIndex !== null ? selectedRowIndex + 1 : flights.length} />}

            {/* 航班编辑表单 */}
            <FlightForm visible={editPopoverVisible} onCancel={handlePopoverClose} onSubmit={handleFormSubmit} flight={selectedFlight} />

            {/* 自动计算配置弹窗 */}
            <Modal
                title="计算配置"
                open={calcModalVisible}
                onCancel={() => setCalcModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setCalcModalVisible(false)}>
                        关闭
                    </Button>,
                    <Button key="save" type="primary" onClick={() => setCalcModalVisible(false)}>
                        保存设置
                    </Button>,
                ]}
            >
                <p>配置自动计算航班时间的规则，保存后可在任意航班点击"重算"按钮应用。</p>
                <Checkbox checked={calcConfig.considerMinGroundTime} onChange={(e) => setCalcConfig({ ...calcConfig, considerMinGroundTime: e.target.checked })}>
                    考虑最小过站时间（使用机场约束中的设置）
                </Checkbox>
                <br />
                <Checkbox checked={calcConfig.considerCurfew} onChange={(e) => setCalcConfig({ ...calcConfig, considerCurfew: e.target.checked })}>
                    考虑宵禁时间（遇到宵禁自动延后起飞）
                </Checkbox>
                <br />
                <Checkbox checked={calcConfig.considerTransportDemand} onChange={(e) => setCalcConfig({ ...calcConfig, considerTransportDemand: e.target.checked })}>
                    考虑运输需求（出发地、首选日期和首选时间）
                </Checkbox>
                <br />
                <Checkbox checked={calcConfig.minimumImpactMode} onChange={(e) => setCalcConfig({ ...calcConfig, minimumImpactMode: e.target.checked })}>
                    最小影响（如果航班已经回到或早于原始计划时间，则自动停止计算后续航班，以最小化调整范围。）
                </Checkbox>
            </Modal>

            {/* 航班连续性中断弹窗 */}
            <Modal
                title="航班连续性中断"
                open={continuityBreakModal.visible}
                footer={[
                    <Button key="stop" type="default" onClick={() => handleContinuityBreakAction("stop")}>
                        停止计算
                    </Button>,
                    <Button key="continue" type="primary" onClick={() => handleContinuityBreakAction("continue")}>
                        忽略并继续
                    </Button>,
                ]}
                closable={false}
                maskClosable={false}
            >
                {continuityBreakModal.flightInfo && (
                    <div>
                        <p>检测到航班连续性中断：</p>
                        <ul>
                            <li>
                                <strong>上一航班 {continuityBreakModal.flightInfo.prevFlight.flight_number}：</strong>
                                {continuityBreakModal.flightInfo.prevFlight.departure_airport} → {continuityBreakModal.flightInfo.prevFlight.arrival_airport}
                            </li>
                            <li>
                                <strong>当前航班 {continuityBreakModal.flightInfo.currentFlight.flight_number}：</strong>
                                {continuityBreakModal.flightInfo.currentFlight.departure_airport} → {continuityBreakModal.flightInfo.currentFlight.arrival_airport}
                            </li>
                        </ul>
                        <p>这两个航班使用同一架飞机注册号 ({continuityBreakModal.flightInfo.currentFlight.aircraft_reg}) 但出发机场不连续。</p>
                        <p>请选择处理方式：</p>
                        <ul>
                            <li>
                                <strong>忽略并继续：</strong> 不修改航班连续性，继续计算后续航班时间
                            </li>
                            <li>
                                <strong>停止计算：</strong> 在此航班处停止计算，之前计算的结果仍会保留
                            </li>
                        </ul>
                    </div>
                )}
            </Modal>

            {/* 复制航班模态框 */}
            <Modal title="复制选中航班" open={copyModalVisible} onOk={handleCopyFlightsExecute} onCancel={() => setCopyModalVisible(false)} okText="确认复制" cancelText="取消">
                <div style={{ marginBottom: 16 }}>
                    <p>请选择复制后航班的起始日期：</p>
                    <UTCDatePicker value={copyStartDate} onChange={(date) => setCopyStartDate(date)} style={{ width: "100%" }} placeholder="选择起始日期" format="YYYY-MM-DD" />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <p>复制次数：</p>
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <InputNumber min={1} max={12} value={copyCount} onChange={(value) => setCopyCount(Math.max(1, Math.min(12, value || 1)))} style={{ width: "100px" }} suffix="次" />
                        <span style={{ marginLeft: 8, color: "#999" }}>将连续复制多个周期（最多支持12次）</span>
                    </div>
                </div>
                <div>
                    <p>说明：</p>
                    <ul>
                        <li>
                            将复制选中的{selectedRows?.length || 0}个航班，共{copyCount}次
                        </li>
                        <li>将按照原航班日期与选定起始日期的偏移量计算新航班日期</li>
                        <li>例如：如果原航班日期是3号到8号，选择的起始日期是10号，则新航班日期将是10号到15号</li>
                        <li>每次重复复制将在前一次基础上增加7天</li>
                        <li>起飞/到达的小时和分钟保持不变</li>
                        <li>跨天航班的跨天关系将被保留</li>
                    </ul>
                </div>
            </Modal>

            {/* 导入航班模态框 */}
            <Modal
                title={
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span>{importType === "schedule" ? "📋 从其他计划导入航班" : "📊 从执行航班导入"}</span>
                            <InlineHelpButton contentPath="help/2025-7-23_flight-schedule-import-dates-2.md" title="导入航班日期选择说明" tooltip="查看日期选择器的详细说明" />
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {importing && importProgress && (
                                <span style={{ color: "#1890ff", fontSize: 13 }}>
                                    正在匹配航路 {importProgress.current}/{importProgress.total}
                                </span>
                            )}
                            <Button onClick={() => setImportModalVisible(false)} disabled={importing}>
                                取消
                            </Button>
                            <Button type="primary" onClick={handleImportFlights} loading={importing} disabled={(importType === "schedule" && scheduleFlightsToImport.length === 0) || (importType === "operation" && operationFlights.length === 0) || !importStartDate}>
                                {importing ? "导入中..." : "确认导入"}
                            </Button>
                        </div>
                    </div>
                }
                className="large-modal"
                open={importModalVisible}
                onCancel={() => {
                    if (!importing) setImportModalVisible(false);
                }}
                onOk={handleImportFlights}
                width={800}
                footer={null}
                closable={false}
                bodyStyle={{ paddingTop: 10 }}
            >
                {/* 导入提示 */}
                <Alert message="导入后航班将按起飞时间自动排序，并在飞机注册号列标记批次序号（如 #1, #2），方便识别多次导入" type="info" showIcon style={{ marginBottom: 12 }} />
                {importType === "schedule" ? (
                    <div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                            <div>
                                <span style={{ display: "block", marginBottom: 4 }}>计划状态：</span>
                                <Select
                                    mode="multiple"
                                    allowClear
                                    maxTagCount="responsive"
                                    placeholder="全部状态"
                                    style={{ width: "100%" }}
                                    value={importScheduleStatuses}
                                    options={SCHEDULE_IMPORT_STATUS_OPTIONS}
                                    onChange={handleImportScheduleStatusesChange}
                                />
                            </div>
                            <div>
                                <span style={{ display: "block", marginBottom: 4 }}>飞机注册号：</span>
                                <AircraftSelector
                                    allowClear
                                    valueType="registration"
                                    displayMode="simple"
                                    placeholder="全部飞机"
                                    style={{ width: "100%" }}
                                    value={importScheduleAircraftReg}
                                    onChange={handleImportScheduleAircraftChange}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <span style={{ marginRight: 8 }}>选择计划:</span>
                            <Select
                                showSearch
                                placeholder="输入计划名称或机号搜索"
                                style={{ width: "100%" }}
                                loading={loadingSchedulesToImport}
                                onChange={handleSelectSchedule}
                                value={selectedScheduleIdToImport}
                                filterOption={false}
                                onSearch={(value) => {
                                    setImportScheduleSearchText(value);
                                    clearTimeout(searchTimerRef.current);
                                    searchTimerRef.current = setTimeout(() => fetchSchedules(value, 50), 300);
                                }}
                                notFoundContent={loadingSchedulesToImport ? <Spin size="small" /> : "输入关键词搜索计划"}
                                options={scheduleListToImport.map((s) => ({
                                    value: s.id,
                                    label: `${s.name} [${getScheduleImportStatusLabel(s.status)}] [${s.aircraft_reg || "未分配飞机"}]${s.start_date && s.end_date ? ` (${dayjs(s.start_date).format("MMDD")} ~ ${dayjs(s.end_date).format("MMDD")})` : " (无日期范围)"}${s.tags?.length > 0 ? ` [${s.tags.join(", ")}]` : ""}`,
                                }))}
                            />
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <p>
                                请选择导入后航班的起始日期：
                                <Tooltip title="所有航班日期会相应调整，保持原有航班模式。例如：如果原始航班日期是3月1日至3月5日，选择的起始日期是4月1日，则导入后航班日期将是4月1日至4月5日">
                                    <InfoCircleOutlined style={{ color: "rgba(0,0,0,0.45)", marginLeft: 4 }} />
                                </Tooltip>
                            </p>
                            <UTCDatePicker value={importStartDate} onChange={(date) => setImportStartDate(date)} placeholder="选择导入起始日期" format="YYYY-MM-DD" />
                        </div>

                        {loadingFlightsToImport ? (
                            <div style={{ textAlign: "center", padding: 20 }}>
                                <Spin tip="加载航班数据中..." />
                            </div>
                        ) : scheduleFlightsToImport.length > 0 ? (
                            <div>
                                <p>共找到 {scheduleFlightsToImport.length} 个航班可导入</p>
                                <Table
                                    size="small"
                                    dataSource={scheduleFlightsToImport}
                                    rowKey="id"
                                    sticky={{ offsetHeader: 0 }}
                                    pagination={{ pageSize: 50 }}
                                    columns={[
                                        { title: "序号", width: 50, render: (text, record, index) => index + 1, align: "center" },
                                        { title: "航班号", dataIndex: "flight_number", width: 100 },
                                        { title: "日期", dataIndex: "flight_date", width: 80, render: (text, record) => <FlightDateDisplay etdOrStd={record.std} departureAirportCode={record.departure_airport} originalFlightDateForFallback={record.flight_date} /> },
                                        { title: "出发", dataIndex: "departure_airport", width: 80 },
                                        { title: "到达", dataIndex: "arrival_airport", width: 80 },
                                        {
                                            title: "起飞时间",
                                            dataIndex: "std",
                                            width: 100,
                                            render: (text, record) => <FlightTimeDisplay utcTime={text} airportCode={record.departure_airport} airportsMap={airportsMap} />,
                                        },
                                        {
                                            title: "到达时间",
                                            dataIndex: "sta",
                                            width: 100,
                                            render: (text, record) => <FlightTimeDisplay utcTime={text} airportCode={record.arrival_airport} airportsMap={airportsMap} />,
                                        },
                                    ]}
                                />
                            </div>
                        ) : selectedScheduleIdToImport ? (
                            <Empty description="该草稿计划没有航班数据" />
                        ) : (
                            <Empty description="请先选择一个草稿计划" />
                        )}
                    </div>
                ) : (
                    <div>
                        {/* 选择航班范围区域 */}
                        <div style={{ marginBottom: 24, border: "1px solid #e8e8e8", borderRadius: 6, padding: 16, backgroundColor: "#fafafa" }}>
                            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
                                <span style={{ fontWeight: "bold", fontSize: 16, color: "#1890ff" }}>第一步：选择航班范围</span>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                                <div>
                                    <span style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
                                        选择飞机 <span style={{ color: "red" }}>*</span>
                                    </span>
                                    <AircraftSelector value={importSelectedAircraft} onChange={handleImportAircraftChange} style={{ width: "100%" }} displayMode="detailed" placeholder="请选择飞机" />
                                </div>

                                <div>
                                    <span style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
                                        日期范围{" "}
                                        <Tooltip title="默认范围为计划的日期范围">
                                            <InfoCircleOutlined style={{ color: "rgba(0,0,0,0.45)" }} />
                                        </Tooltip>
                                    </span>
                                    <DatePicker.RangePicker style={{ width: "100%" }} value={importDateRange.startDate && importDateRange.endDate ? [importDateRange.startDate, importDateRange.endDate] : undefined} onChange={handleDateRangeChange} allowClear={false} format="YYYY-MM-DD" placeholder={["开始日期", "结束日期"]} />
                                </div>
                            </div>
                        </div>

                        {/* 导入设置区域 - 用不同颜色的Card分组 */}
                        <div style={{ marginBottom: 16, border: "1px solid #52c41a", borderRadius: 6, padding: 16, backgroundColor: "#f6ffed" }}>
                            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
                                <span style={{ fontWeight: "bold", fontSize: 16, color: "#52c41a" }}>第二步：设置导入参数</span>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "end" }}>
                                <div>
                                    <span style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>
                                        导入起始日期 <span style={{ color: "red" }}>*</span>
                                        <Tooltip
                                            title={
                                                <div>
                                                    所有航班日期会<strong>整体平移</strong>，保持原有航班模式。
                                                    <br />
                                                    例如：如果原始航班日期是3月1日至3月5日，选择导入起始日期为4月1日，
                                                    <br />
                                                    则导入后航班日期将是4月1日至4月5日
                                                </div>
                                            }
                                        >
                                            <InfoCircleOutlined style={{ color: "rgba(0,0,0,0.45)", marginLeft: 4 }} />
                                        </Tooltip>
                                    </span>
                                    <UTCDatePicker value={importStartDate} onChange={(date) => setImportStartDate(date)} style={{ width: "100%" }} placeholder="选择导入后航班的开始日期" format="YYYY-MM-DD" />
                                </div>

                                <div style={{ marginBottom: 5 }}>
                                    <Checkbox checked={importRemarks} onChange={(e) => setImportRemarks(e.target.checked)}>
                                        导入原有备注信息
                                        <Tooltip title="如选中将保留执行计划中的备注内容，否则备注字段将为空">
                                            <InfoCircleOutlined style={{ color: "rgba(0,0,0,0.45)", marginLeft: 4 }} />
                                        </Tooltip>
                                    </Checkbox>
                                </div>
                            </div>
                        </div>

                        {loadingFlightsToImport ? (
                            <div style={{ textAlign: "center", padding: 20 }}>
                                <Spin tip="正在加载执行航班数据..." />
                            </div>
                        ) : operationFlights.length > 0 ? (
                            <div>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                    <span>
                                        📊 查询结果：共找到 <strong>{operationFlights.length}</strong> 个执行航班可导入
                                    </span>
                                    <Tag color="green">待导入</Tag>
                                </div>
                                <div style={{ border: "1px solid #e8e8e8", borderRadius: 4 }}>
                                    <Table
                                        size="small"
                                        dataSource={operationFlights}
                                        rowKey="id"
                                        sticky={{ offsetHeader: 0 }}
                                        pagination={{ pageSize: 50, size: "small" }}
                                        scroll={{ x: 860 }}
                                        columns={[
                                            { title: "序号", width: 50, render: (text, record, index) => index + 1, align: "center" },
                                            { title: "航班号", dataIndex: "flight_number", width: 100 },
                                            {
                                                title: "日期",
                                                dataIndex: "flight_date",
                                                width: 80,
                                                render: (text, record) => (
                                                    <FlightDateDisplay
                                                        etdOrStd={pickEditorDepartureTime(record)}
                                                        departureAirportCode={record.departure_airport}
                                                        originalFlightDateForFallback={record.flight_date}
                                                    />
                                                ),
                                            },
                                            {
                                                title: "导入后日期",
                                                width: 110,
                                                render: (text, record) => {
                                                    const importedStd = TableService.getImportedStdPreview(record, operationImportDateOffsetDays);
                                                    return importedStd ? <FlightDateDisplay etdOrStd={importedStd} departureAirportCode={record.departure_airport} targetFormat="YYYY-MM-DD" /> : "--";
                                                },
                                            },
                                            { title: "出发", dataIndex: "departure_airport", width: 80 },
                                            { title: "到达", dataIndex: "arrival_airport", width: 80 },
                                            {
                                                title: "起飞时间",
                                                key: "departure_time_preferred",
                                                width: 100,
                                                render: (_, record) => (
                                                    <FlightTimeDisplay
                                                        utcTime={pickEditorDepartureTime(record)}
                                                        airportCode={record.departure_airport}
                                                        airportsMap={airportsMap}
                                                    />
                                                ),
                                            },
                                            {
                                                title: "到达时间",
                                                key: "arrival_time_preferred",
                                                width: 100,
                                                render: (_, record) => (
                                                    <FlightTimeDisplay
                                                        utcTime={pickEditorArrivalTime(record)}
                                                        airportCode={record.arrival_airport}
                                                        airportsMap={airportsMap}
                                                    />
                                                ),
                                            },
                                            { title: "状态", dataIndex: "status", width: 100, render: (text) => <Tag color="blue">{text}</Tag> },
                                        ]}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
                                <Empty
                                    description={
                                        <div>
                                            <div style={{ fontSize: 16, marginBottom: 8 }}>暂无执行航班数据</div>
                                            <div style={{ fontSize: 14 }}>请先设置查询条件并点击“查询执行航班”</div>
                                        </div>
                                    }
                                />
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            <V2TrackImportModal
                open={v2TrackImportVisible}
                schedule={schedule}
                onCancel={() => setV2TrackImportVisible(false)}
                onImportRows={handleImportV2TrackRows}
            />

            {/* 通用导入模态框（新增） */}
            <ImportModal
                isOpen={importFromTextVisible}
                onClose={() => setImportFromTextVisible(false)}
                importType="flexible_flight_schedule_import"
                onDataParsed={handleDataParsedFromImport}
                onError={(error) => {
                    console.error("导入解析错误:", error);
                    message.error("导入解析失败: " + (error.message || "未知错误"));
                }}
            />

            <style jsx>{`
                /* 选中行样式 */
                .row-selected td {
                    background-color: #e6f7ff !important;
                }
                /* 行号样式 */
                .row-number-with-checkbox {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    cursor: pointer;
                    padding: 8px 0;
                }
                .row-number {
                    display: inline-block;
                }
                /* 隐藏表格默认的选择框 */
                .ant-table-selection-column {
                    width: 0 !important;
                    padding: 0 !important;
                }
                .ant-table-selection-column .ant-checkbox-wrapper {
                    display: none !important;
                }
                .ant-table-wrapper {
                    padding: 0;
                }
                .ant-table {
                    min-height: 400px;
                }
                .ant-table-container {
                    min-height: 400px;
                    display: flex;
                    flex-direction: column;
                }
                .ant-table-body {
                    flex: 1;
                    min-height: 300px;
                }
                .ant-empty {
                    padding: 50px 0;
                }
                .custom-empty-container {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 300px;
                    background: #fafafa;
                    border-radius: 2px;
                    border: 1px dashed #d9d9d9;
                    margin: 10px 0;
                }
                .form-inline-group {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    grid-gap: 16px;
                }
                .add-button-row td {
                    padding: 8px !important;
                }
                .batch-operations-buttons {
                    display: flex;
                    gap: 8px;
                    margin-left: 16px;
                }

                /* 导入下拉菜单样式 */
                .import-dropdown-trigger:hover + .import-dropdown,
                .import-dropdown:hover {
                    display: block !important;
                }

                .import-dropdown {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    background: #fff;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                    border-radius: 4px;
                    padding: 4px;
                    z-index: 1000;
                    min-width: 180px;
                }

                /* 高亮行样式 */
                .row-highlighted td {
                    background-color: #fffbe6 !important; /* 淡黄色背景 */
                    transition: background-color 0.5s ease-out;
                }

                /* 确保默认行也有过渡效果，以便平滑恢复 */
                .ant-table-row td {
                    transition: background-color 0.5s ease-out;
                }

                /* 筛选面板样式 */
                .ant-collapse-ghost > .ant-collapse-item > .ant-collapse-header {
                    padding: 8px 0;
                }

                .ant-collapse-ghost > .ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box {
                    padding: 16px 0;
                }
            `}</style>
        </div>
    );
};

// 使用App.useApp包装组件
const FlightScheduleTableWithApp = (props) => {
    return (
        <App>
            <FlightScheduleTable {...props} />
        </App>
    );
};

export default FlightScheduleTableWithApp;
